/*
 * assets/product-magnetic-set.js
 *
 * Picker JS for sections/product-magnetic-set.liquid.
 * Steps 1-5: Case Count -> Case Size -> per-slot Case Color ->
 * per-slot Lid Engraving -> In-Pocket Engraving. An optional setting-gated
 * step 6 adds the size- and count-matched Ultem lid set.
 *
 * Depends on (loaded before this file by the section):
 *   - theme.productDataFetcher  (assets/product-data-fetcher.js)
 *   - theme.twoSideCart         (assets/two-side-cart.js)
 *
 * Contracts (see the plan + cart-transform/magnetic-set.ts):
 *   - ROLE_LAYOUT order MUST match the transform: the c array is emitted in
 *     [left, (middle...), right] order per count.
 *   - Cart write emits ONE tier line with exactly these properties:
 *       _ikg_bundle    = "mset2:" + bundleId
 *       _magnetic_set  = JSON {c,l,p,ps,cc,bc,tc,lc,pc,uc,lbc,pbc,ubc,lv,pv,uv,pkv}
 *       Magnetic Set # = String(getNextMagneticSetNumber(currentCartItems))
 *     The retired legacy identity property is never written.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-magnetic-set-root]';

  // Base USD prices by selected case count. Keep this table aligned with the
  // count-specific Lid and In-Pocket engraving variants in Shopify.
  var USD_ADDON_CENTS_BY_COUNT = {
    1: { lid: 1500, pocket: 1000 },
    2: { lid: 1500, pocket: 1000 },
    3: { lid: 1000, pocket: 1500 },
    4: { lid: 1000, pocket: 1500 }
  };

  // Added Ultem lids are priced per lid and per size on `ultem-lids`, so their
  // USD base is a size price times the case count. A count-keyed total (the
  // former `ultem` column, flat $20 per lid) undercharged every Vitamin and
  // Vitamin XL set whenever checkout converted through USD.
  var USD_ULTEM_LID_CENTS_BY_SIZE = {
    pill: 2000,
    vitamin: 2050,
    vitamin_xl: 2300
  };

  function usdUltemSetCents(count, sizeCategory) {
    var normalizedCount = parseInt(count, 10) || 0;
    var perLidCents = USD_ULTEM_LID_CENTS_BY_SIZE[sizeCategory];
    if (normalizedCount < 1 || normalizedCount > 4 || !perLidCents) return 0;
    return perLidCents * normalizedCount;
  }

  // Physical layout per count. MUST equal ROLE_LAYOUT in the cart transform.
  var ROLE_LAYOUT = {
    1: ['middle'],
    2: ['left', 'right'],
    3: ['left', 'middle', 'right'],
    4: ['left', 'middle', 'middle', 'right']
  };

  var COUNT_SELECTION_SUMMARIES = {
    1: '1 Magnetic Middle Piece',
    2: '2-Case Set',
    3: '3-Case Set',
    4: '4-Case Set'
  };

  // Per-slot labels. Identical to the cart rendering (do not drift).
  var SLOT_LABELS = {
    1: ['Magnetic Middle Case'],
    2: ['AM - Left Side', 'PM - Right Side'],
    3: ['Left Case', 'Middle Case', 'Right Case'],
    4: ['Left Case', 'Middle Case 1', 'Middle Case 2', 'Right Case']
  };

  var WEEKDAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

  var POCKET_PRESETS = {
    WEEKLY: {
      2: ['Week 1', 'Week 2'],
      3: ['Week 1', 'Week 2', 'Week 3'],
      4: ['Week 1', 'Week 2', 'Week 3', 'Week 4']
    },
    DAILY: {
      2: ['AM', 'PM'],
      3: ['Morn', 'Noon', 'Night'],
      4: ['Morn', 'Noon', 'Eve', 'Bed']
    }
  };

  // A one-case MIDDLE may extend or replace part of an existing assembly, so
  // count cannot determine its pocket identity. Keep this as the single source
  // for option rendering, validation, preview text, and payload serialization.
  var SINGLE_POCKET_LABEL_GROUPS = [
    { label: 'Weekly', values: ['Week 1', 'Week 2', 'Week 3', 'Week 4'] },
    { label: 'Daily', values: ['AM', 'PM', 'Noon', 'Eve'] }
  ];
  var SINGLE_POCKET_LABELS = SINGLE_POCKET_LABEL_GROUPS.reduce(function (labels, group) {
    return labels.concat(group.values);
  }, []);

  function sortColorsByAvailability(colors) {
    var list = Array.isArray(colors) ? colors : [];
    return list.map(function (color, sourceIndex) {
      var rawSortIndex = color && color.sortIndex;
      var parsedSortIndex = rawSortIndex === null || rawSortIndex === undefined || String(rawSortIndex).trim() === ''
        ? null
        : Number(rawSortIndex);
      return {
        color: color,
        sourceIndex: sourceIndex,
        sortIndex: parsedSortIndex !== null && isFinite(parsedSortIndex) ? parsedSortIndex : null
      };
    }).sort(function (left, right) {
      if (!!left.color.unavailable !== !!right.color.unavailable) return left.color.unavailable ? 1 : -1;
      if (left.sortIndex !== null && right.sortIndex !== null && left.sortIndex !== right.sortIndex) {
        return left.sortIndex - right.sortIndex;
      }
      if (left.sortIndex !== null && right.sortIndex === null) return -1;
      if (left.sortIndex === null && right.sortIndex !== null) return 1;
      return left.sourceIndex - right.sourceIndex;
    }).map(function (entry) {
      return entry.color;
    });
  }

  function isAluminumColor(colorName) {
    return String(colorName || '').trim().toLowerCase() === 'aluminum';
  }

  function getLidPreviewLabelStyle(colorName, context) {
    var isThumbnail = context === 'thumb';

    if (isAluminumColor(colorName)) {
      return {
        color: isThumbnail ? '#a3a3a3' : 'transparent',
        textStroke: isThumbnail ? '' : '1px #a3a3a3'
      };
    }

    return {
      color: isThumbnail ? '#e5e5e5' : 'transparent',
      textStroke: isThumbnail ? '' : '1px #d3d3d3'
    };
  }

  function parseInitialCount(search) {
    try {
      var raw = new URLSearchParams(String(search == null ? '' : search)).get('count');
      return /^[1-4]$/.test(String(raw || '')) ? parseInt(raw, 10) : 0;
    } catch (error) {
      return 0;
    }
  }

  function getMagneticDetailsPageHandle(count, pocketMode) {
    var parsedCount = parseInt(count, 10) || 0;
    if (parsedCount < 2 || parsedCount > 4) return 'mset-1x';
    return 'mset-' + parsedCount + 'x-' + (pocketMode === 'DAILY' ? 'daily' : 'weekly');
  }

  function isAllowedSinglePocketLabel(value) {
    return SINGLE_POCKET_LABELS.indexOf(String(value == null ? '' : value)) !== -1;
  }

  function isPocketModeAllowed(count, mode) {
    if (mode === 'NONE') return count >= 1 && count <= 4;
    if (count === 1) return mode === 'SINGLE';
    return count >= 2 && count <= 4 && (mode === 'WEEKLY' || mode === 'DAILY');
  }

  function isPocketEngravingMode(count, mode) {
    return count === 1
      ? mode === 'SINGLE'
      : count >= 2 && count <= 4 && (mode === 'WEEKLY' || mode === 'DAILY');
  }

  function resolvePocketValues(count, mode, singleLabel) {
    if (count === 1 && mode === 'SINGLE') {
      return isAllowedSinglePocketLabel(singleLabel) ? [singleLabel] : [];
    }
    if (count >= 2 && count <= 4 && (mode === 'WEEKLY' || mode === 'DAILY')) {
      return ((POCKET_PRESETS[mode] || {})[count] || []).slice();
    }
    return Array.from({ length: Math.max(0, count || 0) }, function () { return ''; });
  }

  function orderedWeekdays(startDay) {
    var startIndex = WEEKDAYS.indexOf(String(startDay || '').toUpperCase());
    if (startIndex === -1) return [];
    return WEEKDAYS.slice(startIndex).concat(WEEKDAYS.slice(0, startIndex));
  }

  function buildPocketPreviewRows(count, mode, singleLabel, startDay) {
    var normalizedCount = Math.max(0, parseInt(count, 10) || 0);
    var values = resolvePocketValues(normalizedCount, mode, singleLabel);
    var labels = SLOT_LABELS[normalizedCount] || [];
    var weekdays = isPocketEngravingMode(normalizedCount, mode)
      ? orderedWeekdays(startDay)
      : [];
    return Array.from({ length: normalizedCount }, function (_, index) {
      var pocketLabel = String(values[index] || '');
      return {
        index: index,
        slotLabel: labels[index] || ('Case ' + (index + 1)),
        pocketLabel: pocketLabel,
        weekdays: pocketLabel ? weekdays.slice() : []
      };
    });
  }

  function priceCents(variant) {
    var cents = parseInt(variant && variant.price, 10);
    return isFinite(cents) && cents > 0 ? cents : 0;
  }

  function compareAtCents(variant) {
    var current = priceCents(variant);
    var compare = parseInt(variant && variant.compare_at_price, 10);
    return isFinite(compare) && compare > current ? compare : current;
  }

  function computeDisplayTotals(tierVariant, addonEntries) {
    if (!tierVariant) {
      return {
        currentTotalCents: 0,
        compareTotalCents: 0,
        savingsCents: 0,
        addonCurrentCents: 0,
        addonCompareCents: 0,
        hasPriceBasis: false
      };
    }

    var addonCurrent = 0;
    var addonCompare = 0;
    (addonEntries || []).forEach(function (entry) {
      var quantity = Math.max(0, parseInt(entry && entry.quantity, 10) || 0);
      if (!entry || !entry.variant || quantity < 1) return;
      addonCurrent += priceCents(entry.variant) * quantity;
      addonCompare += compareAtCents(entry.variant) * quantity;
    });

    var currentTotal = priceCents(tierVariant) + addonCurrent;
    var compareTotal = compareAtCents(tierVariant) + addonCompare;
    return {
      currentTotalCents: currentTotal,
      compareTotalCents: compareTotal,
      savingsCents: Math.max(0, compareTotal - currentTotal),
      addonCurrentCents: addonCurrent,
      addonCompareCents: addonCompare,
      hasPriceBasis: true
    };
  }

  var CASE_SIZE_OPTION_VALUES = {
    pill: 'Weekly Pill Case',
    vitamin: 'Vitamin Case',
    vitamin_xl: 'Vitamin XL Case'
  };

  function normalizeCatalogOption(value) {
    return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function productOptionName(option) {
    return option && typeof option === 'object' ? option.name : option;
  }

  function resolveSizeCountVariant(product, count, sizeCategory) {
    var normalizedCount = parseInt(count, 10) || 0;
    var sizeValue = CASE_SIZE_OPTION_VALUES[sizeCategory];
    if (normalizedCount < 1 || normalizedCount > 4 || !sizeValue) return null;
    if (!product || !Array.isArray(product.options) || !Array.isArray(product.variants)) return null;

    var optionNames = product.options.map(function (option) {
      return normalizeCatalogOption(productOptionName(option));
    });
    var sizeIndex = optionNames.indexOf('size');
    var countIndex = optionNames.findIndex(function (name) { return name.indexOf('count') !== -1; });
    if (sizeIndex < 0 || countIndex < 0 || sizeIndex === countIndex) return null;

    var countValue = normalizedCount + (normalizedCount === 1 ? ' Case' : ' Cases');
    var expectedSize = normalizeCatalogOption(sizeValue);
    var expectedCount = normalizeCatalogOption(countValue);
    for (var i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i];
      var options = [variant && variant.option1, variant && variant.option2, variant && variant.option3];
      if (
        variant
        && variant.available === true
        && normalizeCatalogOption(options[sizeIndex]) === expectedSize
        && normalizeCatalogOption(options[countIndex]) === expectedCount
      ) {
        return variant;
      }
    }
    return null;
  }

  // `ultem-lids` sells the Magnetic Set packs as one option with a set suffix,
  // under the AM-PM size names that carry the matching SKUs.
  var ULTEM_SET_OPTION_BASE = {
    pill: 'Weekly AM-PM Pill Case',
    vitamin: 'Weekly AM-PM Vitamin Case',
    vitamin_xl: 'Weekly AM-PM Vitamin XL Case'
  };

  function resolveUltemSetVariant(product, count, sizeCategory) {
    var normalizedCount = parseInt(count, 10) || 0;
    var base = ULTEM_SET_OPTION_BASE[sizeCategory];
    if (normalizedCount < 1 || normalizedCount > 4 || !base) return null;
    if (!product || !Array.isArray(product.variants)) return null;

    var expected = normalizeCatalogOption(base + ' (Set of ' + normalizedCount + ')');
    // The listing carries duplicate SKUs across families, so ambiguity fails closed.
    var matches = product.variants.filter(function (variant) {
      return variant
        && variant.available === true
        && normalizeCatalogOption(variant.option1) === expected;
    });
    return matches.length === 1 ? matches[0] : null;
  }

  // The two shapes are mutually exclusive: `ultem-lids` has no count option and
  // the retiring `ultem-lid-magnetic-set` has no set-suffixed values, so either
  // product can be wired to the section without a second code change.
  function resolveUltemVariant(product, count, sizeCategory) {
    return resolveUltemSetVariant(product, count, sizeCategory)
      || resolveSizeCountVariant(product, count, sizeCategory);
  }

  function requiresPackagingForCount(count) {
    var normalizedCount = parseInt(count, 10) || 0;
    return normalizedCount === 1 || normalizedCount === 3 || normalizedCount === 4;
  }

  function resolvePackagingVariant(product, count, sizeCategory) {
    var normalizedCount = parseInt(count, 10) || 0;
    if (!requiresPackagingForCount(normalizedCount)) return null;
    return resolveSizeCountVariant(product, normalizedCount, sizeCategory);
  }

  function buildPreviewAltText(display) {
    if (!display || !display.count) {
      return 'Magnetic Set preview. Select a case count, size, and colors to build your set.';
    }

    var sizeLabel = display.size && (display.size.displayName || display.size.label);
    var heading = display.count + '-case Magnetic Set';
    if (sizeLabel) heading += ', ' + sizeLabel + ' size';

    var slots = (display.colors || []).map(function (slot) {
      var description = slot.label + ': ' + (slot.colorName || 'color not selected');
      var lidText = String(slot.lidText || '').trim();
      if (lidText) description += ', lid engraving ' + lidText;
      return description;
    });
    return heading + (slots.length ? '. ' + slots.join('. ') + '.' : '.');
  }

  // Selection and cart payloads stay in physical bottom-to-top order:
  // LEFT, MIDDLE 1, MIDDLE 2, RIGHT. Preview flex columns render top-to-bottom,
  // so reverse a copy for every visual stack without changing slot identity.
  function getPreviewStackSlots(colors) {
    return (Array.isArray(colors) ? colors : []).slice().reverse();
  }

  function getStorefrontInventoryFloor(variant) {
    var parsedQuantity = parseInt(variant && variant.inventory_quantity, 10);
    var quantity = isFinite(parsedQuantity) ? Math.max(0, parsedQuantity) : 0;
    return variant && variant.available === true && quantity < 1 ? 1 : quantity;
  }

  function initSection(section) {
    var moneyFormat = (window.theme && window.theme.moneyFormat) || '${{amount}}';

    function q(sel) { return section.querySelector(sel); }
    function qa(sel) { return section.querySelectorAll(sel); }

    var rolesConfigured = section.getAttribute('data-roles-configured') === 'true';
    var sectionId = section.getAttribute('data-section-id');
    var lidMaxLength = Math.max(1, parseInt(section.getAttribute('data-lid-max-length'), 10) || 12);
    var sizeCardSourceHandle = section.getAttribute('data-size-card-source') || '';
    var listingContext = section.getAttribute('data-listing-context') || '';
    var roleHandles = {
      left: section.getAttribute('data-role-left') || '',
      middle: section.getAttribute('data-role-middle') || '',
      right: section.getAttribute('data-role-right') || ''
    };
    var atcBtn = q('[data-atc]');
    var caseSpecsEl = q('[data-magnetic-set-case-specs]');
    var caseSpecPanels = caseSpecsEl ? caseSpecsEl.querySelectorAll('[data-magnetic-set-case-spec-panel]') : [];
    var galleryStage = q('[data-gallery-stage]');
    var galleryPrevButton = q('[data-gallery-prev]');
    var galleryNextButton = q('[data-gallery-next]');
    var primarySlide = q('[data-gallery-slide][data-slide-index="0"]');
    var primaryThumb = q('[data-gallery-thumb][data-slide-target="0"]');
    var primaryMedia = q('[data-primary-media]');
    var primaryThumbImage = q('[data-primary-thumb-image]');
    var primaryPlaceholder = q('[data-primary-placeholder]');
    var mainPreviewFrame = q('[data-set-preview-frame][data-preview-size="main"]');
    var thumbPreviewFrame = q('[data-set-preview-frame][data-preview-size="thumb"]');
    var initialPrimaryMediaSrc = primaryMedia ? primaryMedia.getAttribute('src') || '' : '';
    var initialPrimaryMediaAlt = primaryMedia ? primaryMedia.getAttribute('alt') || '' : '';
    var initialPrimaryThumbSrc = primaryThumbImage ? primaryThumbImage.getAttribute('src') || '' : '';
    var initialPrimaryLabel = primarySlide ? primarySlide.getAttribute('aria-label') || 'Magnetic Set' : 'Magnetic Set';
    var notifyModalEl = q('[data-notify-modal]');
    var notifyFormEl = q('[data-notify-form]');
    var notifyCountSelect = q('[data-notify-count]');
    var notifySizeSelect = q('[data-notify-size]');
    var notifyRoleSelect = q('[data-notify-role]');
    var notifyColorSelect = q('[data-notify-color]');
    var notifyEmailInput = q('[data-notify-email]');
    var notifyMessageEl = q('[data-notify-message]');
    var notifySubmitBtn = q('[data-notify-submit]');
    var notifyContextEl = q('[data-notify-context]');
    var notifyProductInput = q('[data-notify-product]');
    var notifyVariantLabelInput = q('[data-notify-variant-label]');
    var notifyDesignInput = q('[data-notify-design]');
    var notifyCaseCountInput = q('[data-notify-case-count-value]');
    var notifyPocketCountInput = q('[data-notify-pocket-count-value]');
    var notifyCaseSizeInput = q('[data-notify-case-size-value]');
    var notifyCasePositionInput = q('[data-notify-case-position-value]');
    var notifyColorInput = q('[data-notify-color-value]');
    var notifyPositionCellInput = q('[data-notify-position-cell]');
    var notifyDateInput = q('[data-notify-date]');
    var NOTIFY_ENDPOINT = 'https://script.google.com/macros/s/AKfycbxmSIHmqUAYAX_3xFOZRzjvnzAZGrmCOmyvQkvcxZwvTH8uvIQJ83NCNFgIieZ-7-jP8Q/exec';

    var sizeStep = window.theme && window.theme.sizeStep;
    var pillCounts = readInlineJson('magnetic-set-pill-counts-' + sectionId) || {};
    var sizeCapacityBar = null;
    var tier = readInlineJson('magnetic-set-tier-' + sectionId);
    var lidProduct = readInlineJson('magnetic-set-lid-' + sectionId);
    var pocketProduct = readInlineJson('magnetic-set-pocket-' + sectionId);
    var pocketImagesByColor = readInlineJson('magnetic-set-pocket-images-' + sectionId) || {};
    var caseImagesByColor = readInlineJson('case-images-json-' + sectionId) || {};
    var ultemProduct = readInlineJson('magnetic-set-ultem-' + sectionId);
    var packagingProduct = readInlineJson('magnetic-set-packaging-' + sectionId);
    var addonConfig = readInlineJson('magnetic-set-addon-config-' + sectionId) || {};
    var ultemVisible = addonConfig.ultemVisible === true;
    var ultemSwapConfig = addonConfig.ultemSwap || {};
    var ultemSwapEnabledBySetting = ultemSwapConfig.enabled === true;
    var ultemCaseHandle = String(ultemSwapConfig.handle || '').trim();
    var ultemCaseUrl = String(ultemSwapConfig.url || '').trim();
    // Step 6 exists when either choice is on offer: extra lids, or a swap.
    var ultemStepVisible = ultemVisible || ultemSwapEnabledBySetting;
    var galleryBackgroundSrc = addonConfig.galleryBackgroundSrc || '';
    var galleryBackgroundThumbSrc = addonConfig.galleryBackgroundThumbSrc || galleryBackgroundSrc;
    var previewWidthQuery = window.matchMedia ? window.matchMedia('(max-width: 767px)') : null;

    var state = {
      expandedStep: '1',
      touched: { '1': false, '2': false, '3': false, '4': false, '5': false, '6': false },
      count: 0,
      size: null,      // { key, label, category, displayName, thumbnail, blurb, bestSeller }
      colors: [],      // per slot: { colorKey, colorName, variantId } | undefined
      lid: { mode: null, values: [] },
      pocket: { mode: null, startDay: 'SUN', singleLabel: '' },
      ultem: { mode: 'NONE' }
    };
    var rolePayloads = {};   // role -> payload | { error }
    var rolePromises = {};   // role -> in-flight payload request
    var sizePresentationPayload = null;
    var sizePresentationPromise = null;
    var sizes = [];
    var fetchToken = 0;
    var availabilityToken = 0;
    var availabilityLoading = false;
    var cartReservedByVariantId = {};
    var cartReservationsReady = false;
    var cartReservationsPromise = null;
    var cartCurrency = '';
    var openColorPopup = null;
    var colorPopupListenersBound = false;
    var activeGalleryIndex = 0;
    var gallerySwipeState = null;
    var previewRenderSignature = '';
    var currentTooltip = null;
    var currentTooltipTrigger = null;
    var detailsCurrentPage = null;
    var detailsRequestedPage = null;
    var detailsFetchToken = 0;
    var detailsInitialStateHandled = false;
    var notifyProductFetchToken = 0;
    var notifyReturnFocusEl = null;
    var notifyRoleLabelOverride = '';
    var notifyRequestedColor = '';

    // ---- helpers ----------------------------------------------------------

    function readInlineJson(id) {
      var el = document.getElementById(id);
      if (!el || !el.textContent.trim()) return null;
      try { return JSON.parse(el.textContent); }
      catch (e) { console.warn('[magnetic-set] JSON parse failed for #' + id + ': ' + e.message); return null; }
    }

    function setNotifyMessage(text, tone) {
      if (!notifyMessageEl) return;
      notifyMessageEl.textContent = text || '';
      notifyMessageEl.classList.toggle('tw:hidden', !text);
      notifyMessageEl.classList.remove('tw:text-red-700', 'tw:text-green-700', 'tw:text-gray-600');
      if (!text) return;
      notifyMessageEl.classList.add(tone === 'success' ? 'tw:text-green-700' : (tone === 'error' ? 'tw:text-red-700' : 'tw:text-gray-600'));
    }

    function setNotifySubmitting(isSubmitting) {
      if (!notifySubmitBtn) return;
      notifySubmitBtn.disabled = !!isSubmitting;
      notifySubmitBtn.textContent = isSubmitting ? 'Sending...' : 'Notify me';
    }

    function updateNotifySelectTone(select) {
      if (!select) return;
      var hasValue = !!select.value;
      select.classList.toggle('tw:text-gray-400', !hasValue);
      select.classList.toggle('tw:text-gray-900', hasValue);
    }

    function replaceNotifyOptions(select, options, placeholder, selectedValue) {
      if (!select) return;
      select.innerHTML = '';
      var placeholderOption = document.createElement('option');
      placeholderOption.value = '';
      placeholderOption.textContent = placeholder || 'Select an option';
      placeholderOption.className = 'tw:text-gray-400';
      select.appendChild(placeholderOption);
      (options || []).forEach(function (option) {
        var el = document.createElement('option');
        el.value = option.value;
        el.textContent = option.label;
        el.className = 'tw:text-gray-900';
        if (selectedValue && option.value === selectedValue) el.selected = true;
        select.appendChild(el);
      });
      select.disabled = !options || options.length === 0;
      updateNotifySelectTone(select);
    }

    function getNotifySelectedLabel(select) {
      if (!select || !select.value || select.selectedIndex < 0) return '';
      var option = select.options[select.selectedIndex];
      return option ? String(option.textContent || '').trim() : '';
    }

    function getNotifyCountChoices() {
      return Object.keys(ROLE_LAYOUT).map(function (count) {
        var parsed = parseInt(count, 10) || 0;
        return {
          value: String(parsed),
          label: parsed + (parsed === 1 ? ' Case' : ' Cases')
        };
      });
    }

    function getNotifySizeChoices() {
      return (sizes || []).map(function (size) {
        return {
          value: size.key,
          label: size.displayName || size.label || size.key
        };
      });
    }

    function getNotifySize(sizeKey) {
      var key = String(sizeKey || '');
      return (sizes || []).filter(function (size) { return size.key === key; })[0] || null;
    }

    function getNotifyRoleLabel(role, count) {
      if (role === 'middle') return count === 1 ? 'Magnetic Middle Case' : 'Middle Case';
      if (role === 'left') return count === 2 ? 'AM - Left Side' : 'Left Case';
      if (role === 'right') return count === 2 ? 'PM - Right Side' : 'Right Case';
      return String(role || '').toUpperCase();
    }

    function getNotifyRoleChoices(count, selectedRole) {
      var seen = {};
      return (ROLE_LAYOUT[count] || []).reduce(function (choices, role) {
        if (seen[role]) return choices;
        seen[role] = true;
        choices.push({
          value: role,
          label: notifyRoleLabelOverride && role === selectedRole
            ? notifyRoleLabelOverride
            : getNotifyRoleLabel(role, count)
        });
        return choices;
      }, []);
    }

    function getNotifySoldOutColors(role, size) {
      var seen = {};
      var excluded = {};
      var variants = roleVariantsForSize(role, size);
      // Size-category aliases may match several rows. Any sellable SKU keeps
      // that color out of restock signup, including the hidden-row fallback.
      variants.forEach(function (variant) {
        var key = variant.color_key || handleize(variant.color_name);
        if (!variant.sold_out || variant.bis === false) excluded[key] = true;
      });
      var choices = variants.reduce(function (colors, variant) {
        var colorName = String(variant.color_name || '').trim();
        var colorKey = variant.color_key || handleize(colorName);
        if (!colorName || !colorKey || seen[colorKey]) return colors;
        if (excluded[colorKey]) return colors;
        seen[colorKey] = true;
        colors.push({
          value: colorName,
          label: colorName,
          sortIndex: variant.sort_index,
          unavailable: true
        });
        return colors;
      }, []);
      var payload = rolePayloads[role];
      ((payload && payload.notify_colors) || []).forEach(function (color) {
        if (!size || !((color.size_key && color.size_key === size.key)
          || (size.category && parseCategory(color.size_label) === size.category))) return;
        var name = String(color.color_name || '').trim();
        var key = color.color_key || handleize(name);
        if (!name || !key || seen[key] || excluded[key]) return;
        seen[key] = true;
        choices.push({ value: name, label: name, sortIndex: color.sort_index, unavailable: true });
      });
      return sortColorsByAvailability(choices);
    }

    function getNotifyCaseSizeLabel(sizeKey) {
      var size = getNotifySize(sizeKey);
      if (!size) return '';
      // Card labels can be abbreviated ("Pill") or come from the AM-PM
      // presentation catalog. Use the actual case type in notification names.
      if (size.category === 'pill') return 'Pill Case';
      if (size.category === 'vitamin') return 'Vitamin Case';
      if (size.category === 'vitamin_xl') return 'Vitamin XL Case';
      return String(size.displayName || size.label || '').trim();
    }

    function getNotifyProductName(count, sizeLabel) {
      var normalizedCount = parseInt(count, 10) || 0;
      if (!ROLE_LAYOUT[normalizedCount] || !sizeLabel) return '';
      return normalizedCount + '-Week Magnetic Set ' + sizeLabel;
    }

    function syncNotifyHiddenFields() {
      var count = notifyCountSelect ? parseInt(notifyCountSelect.value, 10) || 0 : 0;
      var sizeLabel = getNotifyCaseSizeLabel(notifySizeSelect ? notifySizeSelect.value : '');
      var productName = getNotifyProductName(count, sizeLabel);
      var roleLabel = getNotifySelectedLabel(notifyRoleSelect);
      var colorLabel = getNotifySelectedLabel(notifyColorSelect);
      var variantLabel = [productName, roleLabel, colorLabel].filter(Boolean).join(' / ');
      var designDetails = [
        productName ? 'Case Count: ' + productName : '',
        sizeLabel ? 'Case Size: ' + sizeLabel : '',
        roleLabel ? 'Case Position: ' + roleLabel : '',
        colorLabel ? 'Color: ' + colorLabel : ''
      ].filter(Boolean).join(' / ');

      updateNotifySelectTone(notifyCountSelect);
      updateNotifySelectTone(notifySizeSelect);
      updateNotifySelectTone(notifyRoleSelect);
      updateNotifySelectTone(notifyColorSelect);
      if (notifyProductInput) notifyProductInput.value = productName;
      if (notifyVariantLabelInput) notifyVariantLabelInput.value = variantLabel;
      if (notifyDesignInput) notifyDesignInput.value = designDetails;
      if (notifyCaseCountInput) notifyCaseCountInput.value = productName;
      if (notifyPocketCountInput) notifyPocketCountInput.value = count ? String(count) : '';
      if (notifyCaseSizeInput) notifyCaseSizeInput.value = sizeLabel;
      if (notifyCasePositionInput) notifyCasePositionInput.value = roleLabel;
      if (notifyColorInput) notifyColorInput.value = colorLabel;
      // Reuse the AM-PM Google Sheets contract: Product, ColorVariant_1,
      // ColorVariant_2. Magnetic Set tracks one case, so the second variant
      // cell holds its position. CaseSize/CasePosition alone are not mapped
      // to cells by the existing sheet integration.
      if (notifyPositionCellInput) notifyPositionCellInput.value = roleLabel;
      if (notifyDateInput) notifyDateInput.value = new Date().toISOString().split('T')[0];
      if (notifyContextEl) {
        notifyContextEl.textContent = productName && sizeLabel && roleLabel && colorLabel
          ? 'We will email you when ' + variantLabel + ' is available.'
          : 'Select the Magnetic Set case you want us to track.';
      }
    }

    function refreshNotifyColorOptions(selectedColor) {
      if (!notifyColorSelect) return;
      var role = notifyRoleSelect ? notifyRoleSelect.value : '';
      var sizeKey = notifySizeSelect ? notifySizeSelect.value : '';
      var size = getNotifySize(sizeKey);
      var requestedColor = selectedColor || notifyRequestedColor || '';
      var token = ++notifyProductFetchToken;
      if (!sizeKey) {
        replaceNotifyOptions(notifyColorSelect, [], 'Select a case size first', '');
        syncNotifyHiddenFields();
        return;
      }
      if (!role) {
        replaceNotifyOptions(notifyColorSelect, [], 'Select a case position first', '');
        syncNotifyHiddenFields();
        return;
      }

      replaceNotifyOptions(notifyColorSelect, [], 'Loading sold-out colors...', '');
      syncNotifyHiddenFields();
      var retryFailedPayload = !!(rolePayloads[role] && rolePayloads[role].error);
      fetchRolePayload(role, retryFailedPayload).then(function (payload) {
        if (token !== notifyProductFetchToken) return;
        if (!notifyRoleSelect || notifyRoleSelect.value !== role || !notifySizeSelect || notifySizeSelect.value !== sizeKey) return;
        if (!payload || payload.error) {
          replaceNotifyOptions(notifyColorSelect, [], 'Could not load sold-out colors', '');
          setNotifyMessage('Could not load the sold-out colors for this case. Please try again.', 'error');
          syncNotifyHiddenFields();
          return;
        }
        var choices = getNotifySoldOutColors(role, size);
        var selectedChoice = choices.filter(function (choice) {
          return handleize(choice.value) === handleize(requestedColor);
        })[0];
        replaceNotifyOptions(
          notifyColorSelect,
          choices,
          choices.length ? 'Select a sold-out color' : 'No sold-out colors for this case',
          selectedChoice ? selectedChoice.value : ''
        );
        if (requestedColor && !selectedChoice) {
          var requested = roleVariantsForSize(role, size).filter(function (variant) {
            return handleize(variant.color_name) === handleize(requestedColor);
          })[0];
          var explanation = requested && !requested.sold_out
            ? requested.color_name + ' is in stock. You can order it now.'
            : (requested && requested.bis === false
              ? 'Restock alerts aren\'t available for ' + requested.color_name + '.'
              : 'The requested color is no longer listed for this selection. Please choose another color.');
          setNotifyMessage(explanation, 'neutral');
        } else if (!choices.length) {
          setNotifyMessage('There are no sold-out colors for this case position and size.', 'neutral');
        }
        notifyRequestedColor = '';
        syncNotifyHiddenFields();
      }).catch(function (error) {
        if (token !== notifyProductFetchToken) return;
        console.error('[magnetic-set] notify role fetch failed:', error);
        replaceNotifyOptions(notifyColorSelect, [], 'Could not load sold-out colors', '');
        setNotifyMessage('Could not load the sold-out colors for this case. Please try again.', 'error');
        syncNotifyHiddenFields();
      });
    }

    function refreshNotifyRoleOptions(selectedRole, selectedColor) {
      if (!notifyRoleSelect) return;
      var count = notifyCountSelect ? parseInt(notifyCountSelect.value, 10) || 0 : 0;
      if (!count) {
        replaceNotifyOptions(notifyRoleSelect, [], 'Select a case count first', '');
        replaceNotifyOptions(notifyColorSelect, [], 'Select a case position first', '');
        syncNotifyHiddenFields();
        return;
      }
      var choices = getNotifyRoleChoices(count, selectedRole);
      var selected = choices.some(function (choice) { return choice.value === selectedRole; }) ? selectedRole : '';
      replaceNotifyOptions(notifyRoleSelect, choices, 'Select a case position', selected);
      refreshNotifyColorOptions(selectedColor || '');
    }

    function refreshNotifySizeOptions(selectedSize, selectedRole, selectedColor) {
      if (!notifySizeSelect) return;
      var count = notifyCountSelect ? parseInt(notifyCountSelect.value, 10) || 0 : 0;
      if (!count) {
        replaceNotifyOptions(notifySizeSelect, [], 'Select a case count first', '');
        replaceNotifyOptions(notifyRoleSelect, [], 'Select a case count first', '');
        replaceNotifyOptions(notifyColorSelect, [], 'Select a case position first', '');
        syncNotifyHiddenFields();
        return;
      }
      var choices = getNotifySizeChoices();
      var selected = choices.some(function (choice) { return choice.value === selectedSize; }) ? selectedSize : '';
      replaceNotifyOptions(notifySizeSelect, choices, choices.length ? 'Select a case size' : 'No case sizes loaded', selected);
      refreshNotifyRoleOptions(selectedRole || '', selectedColor || '');
    }

    function populateNotifyForm(options) {
      if (!notifyFormEl || !notifyCountSelect || !notifySizeSelect || !notifyRoleSelect || !notifyColorSelect) return;
      options = options || {};
      setNotifyMessage('', 'neutral');
      notifyRoleLabelOverride = String(options.slotLabel || '');
      notifyRequestedColor = String(options.color || '');
      var countChoices = getNotifyCountChoices();
      var selectedCount = parseInt(options.count || state.count, 10) || 0;
      var countValue = countChoices.some(function (choice) { return choice.value === String(selectedCount); })
        ? String(selectedCount)
        : '';
      replaceNotifyOptions(notifyCountSelect, countChoices, 'Select a case count', countValue);
      var selectedSize = String(options.size || (selectedCount === state.count && state.size ? state.size.key : '') || '');
      refreshNotifySizeOptions(selectedSize, String(options.role || ''), notifyRequestedColor);
    }

    function focusNotifyModal() {
      var fields = [notifyCountSelect, notifySizeSelect, notifyRoleSelect, notifyColorSelect];
      var target = fields.filter(function (field) { return field && !field.disabled && !field.value; })[0];
      if (!target) target = notifyEmailInput || fields.filter(function (field) { return field && !field.disabled; })[0];
      if (target && typeof target.focus === 'function') target.focus();
    }

    function trapNotifyFocus(event) {
      if (!notifyModalEl || notifyModalEl.getAttribute('data-open') !== 'true') return;
      var focusable = Array.prototype.slice.call(notifyModalEl.querySelectorAll(
        'button:not([disabled]), select:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter(function (element) {
        return element.offsetParent !== null;
      });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !notifyModalEl.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !notifyModalEl.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }

    function openNotifyModal(options) {
      if (!notifyModalEl) return;
      options = options || {};
      notifyReturnFocusEl = options.returnFocus || document.activeElement;
      populateNotifyForm(options);
      notifyModalEl.classList.remove('tw:hidden');
      notifyModalEl.setAttribute('data-open', 'true');
      window.setTimeout(focusNotifyModal, 0);
    }

    function closeNotifyModal() {
      if (!notifyModalEl) return;
      notifyProductFetchToken += 1;
      notifyModalEl.classList.add('tw:hidden');
      notifyModalEl.removeAttribute('data-open');
      setNotifySubmitting(false);
      var returnFocus = notifyReturnFocusEl;
      notifyReturnFocusEl = null;
      if (returnFocus && returnFocus.isConnected && typeof returnFocus.focus === 'function') {
        window.setTimeout(function () { returnFocus.focus(); }, 0);
      }
    }

    function validateNotifyResponse(response) {
      if (!response || response.result !== 'success') {
        throw new Error(response && response.error ? response.error : 'Back-in-stock request failed');
      }
      return response;
    }

    function submitNotifyData(data) {
      if (window.jQuery) {
        return new Promise(function (resolve, reject) {
          window.jQuery.ajax({
            url: NOTIFY_ENDPOINT,
            method: 'GET',
            dataType: 'json',
            data: data
          }).done(function (response) {
            try { resolve(validateNotifyResponse(response)); }
            catch (error) { reject(error); }
          }).fail(reject);
        });
      }
      var params = new URLSearchParams(data);
      return fetch(NOTIFY_ENDPOINT + '?' + params.toString(), { method: 'GET' })
        .then(function (response) {
          if (!response.ok) throw new Error('Notify request failed: ' + response.status);
          return response.json();
        })
        .then(validateNotifyResponse);
    }

    function onNotifySubmit(event) {
      if (!notifyFormEl) return;
      event.preventDefault();
      if (notifySubmitBtn && notifySubmitBtn.disabled) return;
      syncNotifyHiddenFields();
      var count = notifyCountSelect ? notifyCountSelect.value : '';
      var size = notifySizeSelect ? notifySizeSelect.value : '';
      var role = notifyRoleSelect ? notifyRoleSelect.value : '';
      var color = notifyColorSelect ? notifyColorSelect.value : '';
      var email = notifyEmailInput ? String(notifyEmailInput.value || '').trim() : '';
      if (!count || !size || !role || !color || !email || (notifyEmailInput && !notifyEmailInput.checkValidity())) {
        setNotifyMessage('Select the case count, size, position, and sold-out color, then enter a valid email address.', 'error');
        return;
      }
      var data = {
        Product: notifyProductInput ? notifyProductInput.value : '',
        ProductVariantLabel: notifyVariantLabelInput ? notifyVariantLabelInput.value : '',
        DesignDetails: notifyDesignInput ? notifyDesignInput.value : '',
        CaseCount: notifyCaseCountInput ? notifyCaseCountInput.value : '',
        PocketCount: notifyPocketCountInput ? notifyPocketCountInput.value : '',
        CaseSize: notifyCaseSizeInput ? notifyCaseSizeInput.value : '',
        CasePosition: notifyCasePositionInput ? notifyCasePositionInput.value : '',
        ColorVariant_1: notifyColorInput ? notifyColorInput.value : '',
        ColorVariant_2: notifyPositionCellInput ? notifyPositionCellInput.value : '',
        Date: notifyDateInput ? notifyDateInput.value : new Date().toISOString().split('T')[0],
        Email: email
      };
      if (!data.Product || !data.CaseSize || !data.ColorVariant_1 || !data.ColorVariant_2) {
        setNotifyMessage('Select the case count, size, position, and color again before submitting.', 'error');
        return;
      }
      setNotifySubmitting(true);
      setNotifyMessage('', 'neutral');
      submitNotifyData(data).then(function () {
        setNotifyMessage('Thank you. We will notify you when this case is back in stock.', 'success');
        if (notifyEmailInput) notifyEmailInput.value = '';
      }).catch(function (error) {
        console.error('[magnetic-set] notify submit failed:', error);
        setNotifyMessage('There was an error submitting your request. Please try again.', 'error');
      }).then(function () {
        setNotifySubmitting(false);
      }, function () {
        setNotifySubmitting(false);
      });
    }

    function getConfiguratorShopifySection() {
      return section.closest('.shopify-section');
    }

    function getDetailsSection() {
      var host = getConfiguratorShopifySection();
      if (!host) return null;

      var markedSections = document.querySelectorAll('[data-magnetic-set-dynamic-details]');
      for (var i = 0; i < markedSections.length; i++) {
        if (markedSections[i].getAttribute('data-magnetic-set-details-owner') === sectionId) {
          return markedSections[i];
        }
      }

      var sibling = host.nextElementSibling;
      while (sibling && (!sibling.classList || !sibling.classList.contains('shopify-section'))) {
        sibling = sibling.nextElementSibling;
      }
      return sibling && sibling.querySelector('[id^="replo-section-"]') ? sibling : null;
    }

    function extractDetailsSectionFromHtml(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var container = doc.querySelector('#PageContainer') || doc.body;
      if (!container) return null;
      var reploRoot = container.querySelector('.shopify-section [id^="replo-section-"]');
      return reploRoot ? reploRoot.closest('.shopify-section') : null;
    }

    function activateInsertedScripts(container) {
      var scripts = container.querySelectorAll('script');
      for (var i = 0; i < scripts.length; i++) {
        var source = scripts[i];
        var replacement = document.createElement('script');
        for (var j = 0; j < source.attributes.length; j++) {
          var attr = source.attributes[j];
          replacement.setAttribute(attr.name, attr.value);
        }
        replacement.text = source.text || source.textContent || '';
        source.parentNode.replaceChild(replacement, source);
      }
    }

    function markDetailsSection(detailsSection, pageHandle) {
      if (!detailsSection) return;
      detailsSection.setAttribute('data-magnetic-set-dynamic-details', '');
      detailsSection.setAttribute('data-magnetic-set-details-owner', sectionId);
      detailsSection.setAttribute('data-magnetic-set-details-page', pageHandle);
    }

    function replaceDetailsSection(nextSection, pageHandle) {
      var host = getConfiguratorShopifySection();
      if (!host || !nextSection) return false;

      nextSection = nextSection.cloneNode(true);
      markDetailsSection(nextSection, pageHandle);

      var currentDetails = getDetailsSection();
      if (currentDetails) currentDetails.replaceWith(nextSection);
      else host.insertAdjacentElement('afterend', nextSection);

      activateInsertedScripts(nextSection);
      detailsCurrentPage = pageHandle;
      detailsRequestedPage = null;
      document.dispatchEvent(new CustomEvent('magnetic-set:details-loaded', {
        detail: { page: pageHandle, sectionId: sectionId }
      }));
      return true;
    }

    function fetchDetailsPage(pageHandle, token) {
      if (token !== detailsFetchToken) return;
      fetch('/pages/combined-listings?view=' + encodeURIComponent(pageHandle), {
        headers: { 'Accept': 'text/html' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.text();
        })
        .then(function (html) {
          if (token !== detailsFetchToken) return;
          var nextSection = extractDetailsSectionFromHtml(html);
          if (!nextSection) throw new Error('Replo details section not found');
          if (!replaceDetailsSection(nextSection, pageHandle)) {
            throw new Error('Could not replace the Replo details section');
          }
        })
        .catch(function (error) {
          if (token !== detailsFetchToken) return;
          if (detailsRequestedPage === pageHandle) detailsRequestedPage = null;
          console.warn('[magnetic-set] Failed to load details page "' + pageHandle + '":', error);
        });
    }

    function syncDetailsSectionForSelection(count, pocketMode, options) {
      var pageHandle = getMagneticDetailsPageHandle(count, pocketMode);

      if (options && options.initial && pageHandle === 'mset-1x') {
        var initialDetails = getDetailsSection();
        if (initialDetails) {
          markDetailsSection(initialDetails, pageHandle);
          detailsCurrentPage = pageHandle;
          detailsRequestedPage = null;
          return;
        }
      }

      if (detailsCurrentPage === pageHandle) {
        if (detailsRequestedPage && detailsRequestedPage !== pageHandle) {
          detailsFetchToken += 1;
          detailsRequestedPage = null;
        }
        return;
      }
      if (detailsRequestedPage === pageHandle) return;

      detailsRequestedPage = pageHandle;
      fetchDetailsPage(pageHandle, ++detailsFetchToken);
    }

    function onTooltipDocumentClick(event) {
      if (!currentTooltip || !event.target || typeof event.target.closest !== 'function') return;
      if (!event.target.closest('[data-compare-tooltip], .dynamic-tooltip')) closeCompareTooltip();
    }

    function closeCompareTooltip(callback) {
      document.removeEventListener('click', onTooltipDocumentClick);
      if (currentTooltipTrigger) currentTooltipTrigger.setAttribute('aria-expanded', 'false');
      if (!currentTooltip) {
        currentTooltipTrigger = null;
        if (callback) callback();
        return;
      }

      var tooltip = currentTooltip;
      function cleanup() {
        if (tooltip.parentNode) tooltip.parentNode.removeChild(tooltip);
        if (currentTooltip === tooltip) currentTooltip = null;
        currentTooltipTrigger = null;
        if (callback) callback();
      }

      if (window.jQuery) window.jQuery(tooltip).fadeOut(200, cleanup);
      else cleanup();
    }

    function showCompareTooltip(trigger) {
      var tooltipUrl = trigger && trigger.getAttribute('data-tooltip');
      if (!tooltipUrl) return;

      var tooltip = document.createElement('div');
      var tooltipLabel = trigger.getAttribute('data-tooltip-label') || 'Comparison image';
      tooltip.className = 'dynamic-tooltip';
      tooltip.setAttribute('role', 'img');
      tooltip.setAttribute('aria-label', tooltipLabel);
      tooltip.style.backgroundImage = 'url("' + tooltipUrl.replace(/"/g, '\\"') + '")';
      tooltip.style.position = 'absolute';
      tooltip.style.display = 'none';
      document.body.appendChild(tooltip);
      currentTooltip = tooltip;
      currentTooltipTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', onTooltipDocumentClick);

      var viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      var isMobile = viewportWidth <= 768;
      var imgWidth = isMobile ? viewportWidth * 0.85 : 750;
      var img = new Image();

      img.onload = function () {
        if (currentTooltip !== tooltip) return;
        var naturalWidth = img.naturalWidth || img.width || imgWidth;
        var naturalHeight = img.naturalHeight || img.height || imgWidth;
        if (!isMobile && naturalWidth < 750) imgWidth = naturalWidth;
        var imgHeight = (imgWidth / naturalWidth) * naturalHeight;
        var rect = trigger.getBoundingClientRect();
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;

        tooltip.style.width = imgWidth + 'px';
        tooltip.style.height = imgHeight + 'px';
        tooltip.style.top = (rect.top + scrollTop + 20) + 'px';
        tooltip.style.left = Math.max((viewportWidth - imgWidth) / 2, 0) + 'px';

        if (window.jQuery) window.jQuery(tooltip).fadeIn(200);
        else tooltip.style.display = 'block';
      };
      img.onerror = function () {
        if (currentTooltip === tooltip) closeCompareTooltip();
      };
      img.src = tooltipUrl;
    }

    function toggleCompareTooltip(trigger) {
      if (currentTooltipTrigger === trigger) {
        closeCompareTooltip();
        return;
      }
      closeCompareTooltip(function () { showCompareTooltip(trigger); });
    }

    function extractCount(value) {
      var m = String(value == null ? '' : value).match(/(\d+)/);
      return m ? parseInt(m[1], 10) : 0;
    }

    // Mirrors parseCaseSize() in the transform: xl before vitamin, then pill.
    function parseCategory(value) {
      var s = String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
      if (!s) return null;
      if (s.indexOf('vitamin') !== -1) return /\bxl\b/.test(s) ? 'vitamin_xl' : 'vitamin';
      if (s.indexOf('pill') !== -1) return 'pill';
      return null;
    }

    function handleize(value) {
      return String(value == null ? '' : value).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    }

    function displayForCategory(cat) {
      if (cat === 'pill') return 'Pill';
      if (cat === 'vitamin') return 'Vitamin';
      if (cat === 'vitamin_xl') return 'Vitamin XL';
      return null;
    }

    function uniqueRoles(roles) {
      var seen = {}, out = [];
      (roles || []).forEach(function (r) { if (!seen[r]) { seen[r] = true; out.push(r); } });
      return out;
    }

    function normalizeCurrencyCode(value) {
      var code = String(value == null ? '' : value).trim().toUpperCase();
      return /^[A-Z]{3}$/.test(code) ? code : '';
    }

    function normalizeOption(value) {
      return String(value == null ? '' : value).replace(/\s+/g, ' ').trim().toLowerCase();
    }

    function addonValue(count) {
      return 'Magnetic Set - ' + count + (count === 1 ? ' Case' : ' Cases');
    }

    function variantOptions(variant) {
      if (variant && Array.isArray(variant.options)) return variant.options;
      return [variant && variant.option1, variant && variant.option2, variant && variant.option3].filter(Boolean);
    }

    function isAddonVariantSellable(variant) {
      return !!(variant && variant.available === true);
    }

    function findVariantByOptionValue(product, optionValue) {
      if (!product || !Array.isArray(product.variants) || !String(optionValue || '').trim()) return null;
      var expected = normalizeOption(optionValue);
      for (var i = 0; i < product.variants.length; i++) {
        var variant = product.variants[i];
        var exactOption = variantOptions(variant).some(function (value) {
          return normalizeOption(value) === expected;
        });
        if (exactOption && isAddonVariantSellable(variant)) return variant;
      }
      return null;
    }

    function findAddonVariant(product, count) {
      if (!count) return null;
      return findVariantByOptionValue(product, addonValue(count));
    }

    function findUltemVariant() {
      return resolveUltemVariant(ultemProduct, state.count, state.size && state.size.category);
    }

    // ---- Swapped: every case in the set carries an Ultem lid ---------------
    //
    // The set price is this product's own `Ultem` Material variant; the pieces
    // come from the Ultem case listing through theme.ultemCaseCatalog. Both
    // fail closed, so an unconfigured catalog simply never offers the choice.
    var swapState = {
      key: '',
      loading: false,
      ready: false,
      reason: 'unsupported',
      detail: '',
      variants: null,
      engravingVariant: null,
      promise: null
    };
    var warnedUnsellableUltemTier = false;

    function getUltemCatalog() {
      var catalog = window.theme && window.theme.ultemCaseCatalog;
      if (!catalog || !ultemSwapEnabledBySetting || !ultemCaseHandle) return null;
      catalog.configure({ handle: ultemCaseHandle, url: ultemCaseUrl });
      return catalog;
    }

    function getSwapTierVariant() {
      if (!state.count || !state.size || !state.size.category) return null;
      return findTierVariant(state.count, state.size.category, MATERIAL_ULTEM);
    }

    function isSwapSupportedForSelection() {
      // A catalog question, not a stock one: are there Ultem set prices for
      // this count and size? Piece stock is handled separately, so a sold-out
      // color still shows Swap as Sold out rather than hiding it.
      if (!getUltemCatalog()) return false;
      var tierVariant = getSwapTierVariant();
      if (!tierVariant) return false;
      if (!isTierVariantSellable(tierVariant)) {
        if (!warnedUnsellableUltemTier) {
          warnedUnsellableUltemTier = true;
          console.warn('[magnetic-set] The Ultem set variant exists but is not sellable, so Swap is hidden. Turn Track quantity off on the Ultem variants.');
        }
        return false;
      }
      return true;
    }

    function ultemAddonValue(count) {
      return 'Ultem Lid - ' + addonValue(count);
    }

    function findUltemLidEngravingVariant(count) {
      return findVariantByOptionValue(lidProduct, ultemAddonValue(count));
    }

    function getSwapSlots() {
      var catalog = getUltemCatalog();
      if (!catalog || !state.count || !state.size || !state.size.category) return null;
      var roles = ROLE_LAYOUT[state.count] || [];
      var slots = [];
      for (var i = 0; i < state.count; i++) {
        var selected = state.colors[i];
        var role = roles[i];
        if (!selected || !selected.colorName || !role) return null;
        // A middle is priced by the set it belongs to, so its option value
        // differs between a single case and a 3- or 4-case set.
        var spec = role === 'middle'
          ? { role: 'middle', size: state.size.category, count: state.count }
          : { role: role, size: state.size.category };
        if (!catalog.labelFor(spec)) return null;
        slots.push({ index: i, role: role, spec: spec, color: selected.colorName });
      }
      return slots.length === state.count ? slots : null;
    }

    function getSwapKey(slots) {
      var catalog = getUltemCatalog();
      if (!catalog || !slots) return '';
      var tierVariant = getSwapTierVariant();
      return String(tierVariant ? tierVariant.id : '') + '|' + slots.map(function (slot) {
        return slot.index + '::' + catalog.labelFor(slot.spec) + '::' + catalog.colorKey(slot.color);
      }).join('|');
    }

    function resetSwapResolution() {
      swapState.key = '';
      swapState.loading = false;
      swapState.ready = false;
      swapState.reason = 'unsupported';
      swapState.detail = '';
      swapState.variants = null;
      swapState.engravingVariant = null;
      swapState.promise = null;
    }

    function applySwapEntries(slots, entries) {
      var catalog = getUltemCatalog();
      var variants = [];
      var usage = {};
      var byId = {};
      var colorById = {};
      var reason = '';
      var detail = '';

      for (var i = 0; i < slots.length; i++) {
        var entry = entries[i];
        if (!catalog || !entry || !entry.ok) {
          reason = 'error';
          detail = (entry && entry.error) || '';
          break;
        }
        var variant = entry.variantFor(slots[i].color);
        if (!variant) {
          reason = 'color';
          detail = slots[i].color;
          break;
        }
        variants.push({ index: slots[i].index, role: slots[i].role, variant: variant });
        var id = String(variant.id);
        usage[id] = (usage[id] || 0) + 1;
        byId[id] = variant;
        colorById[id] = slots[i].color;
      }

      if (!reason) {
        // Two middles in the same color need two units of one variant, and
        // whatever is already in this cart is subtracted first.
        var shortId = Object.keys(usage).filter(function (id) {
          return getEffectiveVariantCap(byId[id]) < usage[id];
        })[0];
        if (shortId) {
          reason = 'soldout';
          detail = colorById[shortId] || '';
        }
      }

      swapState.loading = false;
      swapState.promise = null;
      swapState.ready = !reason;
      swapState.reason = reason;
      swapState.detail = detail;
      swapState.variants = reason ? null : variants;
      swapState.engravingVariant = reason ? null : findUltemLidEngravingVariant(state.count);
      return swapState;
    }

    function ensureSwapResolution(options) {
      options = options || {};
      var catalog = getUltemCatalog();
      var slots = getSwapSlots();
      if (!catalog || !slots) {
        resetSwapResolution();
        return Promise.resolve(swapState);
      }

      var key = getSwapKey(slots);
      if (!options.force && swapState.key === key) {
        if (swapState.promise) return swapState.promise;
        if (swapState.ready || swapState.reason) return Promise.resolve(swapState);
      }

      resetSwapResolution();
      swapState.key = key;
      swapState.loading = true;
      swapState.reason = 'checking';

      var load = options.force
        ? Promise.all(slots.map(function (slot) { return catalog.refresh(slot.spec); }))
        : catalog.loadMany(slots.map(function (slot) { return slot.spec; }));

      var promise = load.then(function (entries) {
        // A newer count, size or color selection already replaced this request.
        if (swapState.key !== key) return swapState;
        return applySwapEntries(slots, entries);
      }).catch(function () {
        if (swapState.key !== key) return swapState;
        swapState.loading = false;
        swapState.promise = null;
        swapState.ready = false;
        swapState.reason = 'error';
        return swapState;
      });

      swapState.promise = promise;
      return promise;
    }

    function getSwapBlockReason() {
      if (!isSwapSupportedForSelection()) return 'unsupported';
      if (!getSwapSlots()) return 'incomplete';
      if (swapState.loading) return 'checking';
      if (!swapState.ready) return swapState.reason || 'unavailable';
      var engravedLids = state.lid.mode === 'CUSTOM' ? normalizedLidValues().filter(Boolean).length : 0;
      if (engravedLids > 0 && !swapState.engravingVariant) return 'engraving';
      return '';
    }

    function isSwapSelectable() {
      return getSwapBlockReason() === '';
    }

    function getSwapVariantIdsInSlotOrder() {
      if (!swapState.ready || !swapState.variants) return null;
      var ids = [];
      for (var i = 0; i < state.count; i++) {
        var match = swapState.variants.filter(function (slot) { return slot.index === i; })[0];
        if (!match || !match.variant || !match.variant.id) return null;
        ids.push(String(match.variant.id));
      }
      return ids.length === state.count ? ids : null;
    }

    function getSelectedTierVariant() {
      if (!state.count || !state.size) return null;
      if (state.ultem.mode === 'SWAP') return getSwapTierVariant();
      return findTierVariant(state.count, state.size.category);
    }

    function getSwapDeltaCents() {
      var ultemTier = getSwapTierVariant();
      var aluminumTier = (state.count && state.size) ? findTierVariant(state.count, state.size.category) : null;
      if (!ultemTier || !aluminumTier) return null;
      return priceCents(ultemTier) - priceCents(aluminumTier);
    }

    function getSwapUnavailableNote(reason) {
      var colorName = String(swapState.detail || '').trim();
      if (reason === 'checking') return 'Checking Ultem stock...';
      if (reason === 'color') {
        return colorName
          ? 'Ultem isn\'t available in ' + colorName + ' for this size.'
          : 'Ultem isn\'t available in one of your colors for this size.';
      }
      if (reason === 'soldout') {
        return colorName
          ? 'The Ultem case in ' + colorName + ' is sold out for this set. It comes back as soon as stock arrives.'
          : 'One of the Ultem cases is sold out for this set. It comes back as soon as stock arrives.';
      }
      if (reason === 'engraving') return 'Ultem lid engraving isn\'t available for this case count.';
      if (reason === 'error') return 'Ultem stock could not be checked. Please try again.';
      if (reason === 'incomplete') return 'Choose a color for every case to see the Ultem swap.';
      return '';
    }

    function findPackagingVariant() {
      return resolvePackagingVariant(packagingProduct, state.count, state.size && state.size.category);
    }

    function blankValues(count) {
      return Array.from({ length: Math.max(0, count || 0) }, function () { return ''; });
    }

    function normalizedLidValues() {
      var values = blankValues(state.count);
      for (var i = 0; i < values.length; i++) {
        values[i] = String((state.lid.values || [])[i] || '').replace(/\s+/g, ' ').trim().slice(0, lidMaxLength);
      }
      return values;
    }

    function selectedLidCount() {
      return state.lid.mode === 'CUSTOM'
        ? normalizedLidValues().filter(Boolean).length
        : 0;
    }

    function pocketValues() {
      return resolvePocketValues(state.count, state.pocket.mode, state.pocket.singleLabel);
    }

    function isPocketSelected() {
      return isPocketEngravingMode(state.count, state.pocket.mode);
    }

    function hasCompletePocketValues() {
      var values = pocketValues();
      return values.length === state.count && values.every(function (value) { return !!value; });
    }

    function hasValidPocketStart() {
      return WEEKDAYS.indexOf(state.pocket.startDay) !== -1;
    }

    function addonPriceLabel(variant, suffix) {
      var cents = priceCents(variant);
      return cents > 0 ? '(+' + formatMoney(cents) + (suffix || '') + ')' : '';
    }

    // ---- tier lookup (resolve options BY NAME, never by position) ---------

    var tierOptions = (tier && tier.options) || [];
    function findOptionIndex(matcher) {
      for (var i = 0; i < tierOptions.length; i++) {
        if (matcher(String(tierOptions[i] || '').toLowerCase())) return i;
      }
      return -1;
    }
    var countIdx = findOptionIndex(function (n) { return n.indexOf('count') !== -1 || n.indexOf('quantity') !== -1; });
    var sizeIdx = findOptionIndex(function (n) { return n.indexOf('size') !== -1; });
    // The Ultem set prices live on this same product as a Material option, so
    // each count/size cell can have two variants. Without that option every
    // variant is aluminum and every lookup below behaves as it always has.
    var materialIdx = findOptionIndex(function (n) { return n.indexOf('material') !== -1; });

    var MATERIAL_ALUMINUM = 'aluminum';
    var MATERIAL_ULTEM = 'ultem';

    // Unknown values return null and are never matched, so a renamed option
    // value disables Ultem instead of mispricing an aluminum set.
    function tierMaterial(variant) {
      if (materialIdx < 0) return MATERIAL_ALUMINUM;
      var value = String(((variant && variant.options) || [])[materialIdx] || '')
        .toLowerCase()
        .replace(/[^a-z]+/g, '');
      if (value === 'aluminum' || value === 'aluminium') return MATERIAL_ALUMINUM;
      if (value === 'ultem') return MATERIAL_ULTEM;
      return null;
    }

    function findTierVariant(count, sizeCategory, material) {
      var wantedMaterial = material || MATERIAL_ALUMINUM;
      if (!tier || !tier.variants || countIdx < 0 || sizeIdx < 0 || !count || !sizeCategory) return null;
      for (var i = 0; i < tier.variants.length; i++) {
        var v = tier.variants[i];
        var opts = v.options || [];
        if (tierMaterial(v) !== wantedMaterial) continue;
        if (extractCount(opts[countIdx]) === count && parseCategory(opts[sizeIdx]) === sizeCategory) return v;
      }
      return null;
    }

    function isTierVariantSellable(v) {
      return !!(v && v.available === true);
    }

    function hasSellableTierVariantForCount(count) {
      if (!tier || !tier.variants || countIdx < 0) return false;
      return tier.variants.some(function (v) {
        return tierMaterial(v) === MATERIAL_ALUMINUM
          && extractCount((v.options || [])[countIdx]) === count
          && isTierVariantSellable(v);
      });
    }

    function buildSizes() {
      var seen = {}, out = [];
      ((tier && tier.variants) || []).forEach(function (v) {
        if (tierMaterial(v) !== MATERIAL_ALUMINUM) return;
        var label = String((v.options || [])[sizeIdx] || '').trim();
        if (!label) return;
        var key = handleize(label);
        if (seen[key]) return;
        seen[key] = true;
        var category = parseCategory(label);
        out.push({
          key: key,
          label: label,
          category: category,
          displayName: displayForCategory(category) || label,
          thumbnail: '',
          blurb: '',
          bestSeller: false
        });
      });
      return out;
    }

    function findPayloadSize(size, payload) {
      if (!size || !payload || payload.error || !Array.isArray(payload.sizes)) return null;
      for (var i = 0; i < payload.sizes.length; i++) {
        var candidate = payload.sizes[i];
        if (!candidate) continue;
        var candidateKey = handleize(candidate.key || candidate.label || candidate.display_name);
        var candidateCategory = parseCategory(candidate.label || candidate.display_name || candidate.key);
        if (candidateKey === size.key || (size.category && candidateCategory === size.category)) return candidate;
      }
      return null;
    }

    // Tier variants define the selectable size and price. Role payload sizes
    // contribute presentation metadata only; base_price is deliberately ignored.
    function applySizePresentation(preferredRoles) {
      var previousSizes = sizes.slice();
      var roleOrder = uniqueRoles((preferredRoles || []).concat(['left', 'middle', 'right']));
      var nextSizes = buildSizes();

      nextSizes.forEach(function (size) {
        var previous = previousSizes.filter(function (item) { return item.key === size.key; })[0];
        if (previous) {
          size.displayName = previous.displayName || size.displayName;
          size.thumbnail = previous.thumbnail || '';
          size.blurb = previous.blurb || '';
          size.bestSeller = previous.bestSeller === true;
        }

        var metadata = findPayloadSize(size, sizePresentationPayload);
        if (!metadata) {
          for (var i = 0; i < roleOrder.length; i++) {
            metadata = findPayloadSize(size, rolePayloads[roleOrder[i]]);
            if (metadata) break;
          }
        }
        if (metadata) {
          size.displayName = String(metadata.display_name || metadata.label || size.displayName).trim();
          size.thumbnail = String(metadata.thumbnail || '').trim();
          size.blurb = String(metadata.blurb || '').trim();
          size.bestSeller = metadata.best_seller === true;
        }
      });

      sizes = nextSizes;
      if (state.size) {
        state.size = sizes.filter(function (size) { return size.key === state.size.key; })[0] || null;
      }
      updateSizeOptionPresentation();
    }

    // ---- gallery + live set preview --------------------------------------

    function getGallerySlideCount() {
      return qa('[data-gallery-slide]').length;
    }

    function normalizeGalleryIndex(index) {
      var count = getGallerySlideCount();
      if (!count) return 0;
      var parsed = parseInt(index, 10);
      if (!isFinite(parsed)) parsed = 0;
      if (parsed < 0) return count - 1;
      if (parsed >= count) return 0;
      return parsed;
    }

    function updateGalleryControls() {
      var count = getGallerySlideCount();
      var disabled = count <= 1;
      if (galleryPrevButton) galleryPrevButton.disabled = disabled;
      if (galleryNextButton) galleryNextButton.disabled = disabled;
      if (galleryStage) galleryStage.setAttribute('data-gallery-count', String(count));
    }

    function setActiveGallerySlide(index) {
      activeGalleryIndex = normalizeGalleryIndex(index);
      var targetIndex = String(activeGalleryIndex);
      qa('[data-gallery-slide]').forEach(function (slide) {
        var active = slide.getAttribute('data-slide-index') === targetIndex;
        slide.setAttribute('data-active', active ? 'true' : 'false');
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (active) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
        if (!active) {
          slide.querySelectorAll('video').forEach(function (video) {
            if (typeof video.pause === 'function') video.pause();
          });
        }
      });
      qa('[data-gallery-thumb]').forEach(function (thumb) {
        var active = thumb.getAttribute('data-slide-target') === targetIndex;
        thumb.setAttribute('data-active', active ? 'true' : 'false');
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      updateGalleryControls();
    }

    function moveGallery(delta) {
      setActiveGallerySlide(activeGalleryIndex + delta);
    }

    function beginGallerySwipe(event) {
      if (!galleryStage || getGallerySlideCount() <= 1) return;
      if (event.target && event.target.closest && event.target.closest('button, a, input, select, textarea, video, model-viewer')) return;
      if (event.pointerType && event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
      if (typeof event.button === 'number' && event.button !== 0) return;
      gallerySwipeState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY
      };
      if (galleryStage.setPointerCapture && event.pointerId !== undefined) {
        try { galleryStage.setPointerCapture(event.pointerId); }
        catch (error) { /* The browser may already own the touch gesture. */ }
      }
    }

    function endGallerySwipe(event) {
      if (!gallerySwipeState) return;
      if (event.pointerId !== undefined && gallerySwipeState.pointerId !== event.pointerId) return;
      var deltaX = event.clientX - gallerySwipeState.startX;
      var deltaY = event.clientY - gallerySwipeState.startY;
      var absX = Math.abs(deltaX);
      var absY = Math.abs(deltaY);
      if (absX >= 48 && absX > absY * 1.2) moveGallery(deltaX < 0 ? 1 : -1);
      if (galleryStage && galleryStage.releasePointerCapture && event.pointerId !== undefined) {
        try { galleryStage.releasePointerCapture(event.pointerId); }
        catch (error) { /* Pointer capture may already have been released. */ }
      }
      gallerySwipeState = null;
    }

    function cancelGallerySwipe(event) {
      if (!gallerySwipeState) return;
      if (event && event.pointerId !== undefined && gallerySwipeState.pointerId !== event.pointerId) return;
      gallerySwipeState = null;
    }

    function findPreviewVariant(role, slotIndex) {
      var selected = state.colors[slotIndex];
      var payload = rolePayloads[role];
      if (!selected || !payload || payload.error) return null;
      var variants = payload.variants || [];
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(selected.variantId)) return variants[i];
      }
      for (var j = 0; j < variants.length; j++) {
        var variant = variants[j];
        var colorKey = variant.color_key || handleize(variant.color_name);
        if (colorKey === selected.colorKey && matchesSize(variant)) return variant;
      }
      return null;
    }

    function getCaseImageSrc(variant, role) {
      var resolver = window.theme && window.theme.caseImages;
      if (!variant || !resolver) return '';
      return resolver.resolve(caseImagesByColor, {
        colorKey: variant.color_key || variant.color_name,
        sizeKey: variant.size_key || state.size && state.size.key,
        side: role
      });
    }

    function getPreviewCaseSrc(variant, role) {
      return getCaseImageSrc(variant, role)
        || (variant && variant.case_color && variant.case_color.swatch_image)
        || '';
    }

    function getPreviewImageWidth(previewSize) {
      var category = state.size && state.size.category;
      if (previewSize === 'thumb') return category === 'pill' ? '52px' : '58px';
      if (previewSize === 'colorHeader') return category === 'pill' ? '64px' : '72px';
      if (previewSize === 'colorBody') return '120px';
      if (previewWidthQuery && previewWidthQuery.matches) return category === 'pill' ? '232px' : '280px';
      return category === 'pill' ? '350px' : '420px';
    }

    function getPreviewShadowClip(index, count) {
      var bleed = '28px';
      if (count <= 1) return 'inset(-' + bleed + ')';
      if (index === 0) return 'inset(-' + bleed + ' -' + bleed + ' 0 -' + bleed + ')';
      if (index === count - 1) return 'inset(0 -' + bleed + ' -' + bleed + ' -' + bleed + ')';
      return 'inset(0 -' + bleed + ')';
    }

    function createPreviewCaseImage(displaySlot, imageWidth, previewSize, index, count) {
      var image = document.createElement('img');
      var lidText = String(displaySlot.lidText || '').trim();
      if (previewSize === 'colorBody' || previewSize === 'colorHeader') lidText = '';
      image.src = displaySlot.imageUrl;
      image.alt = '';
      image.decoding = 'async';
      image.loading = previewSize === 'main' ? 'eager' : 'lazy';
      image.className = 'tw:block tw:h-auto tw:max-w-none tw:object-contain';
      image.style.width = imageWidth;
      image.style.height = 'auto';
      image.style.maxWidth = 'none';

      if (previewSize !== 'main' && !lidText) return image;

      var clip = document.createElement('span');
      clip.className = 'tw:block tw:relative';
      clip.style.width = imageWidth;
      clip.style.lineHeight = '0';
      if (previewSize === 'main') {
        image.style.borderRadius = state.size && state.size.category === 'pill' ? '6px' : '8px';
        image.style.boxShadow = '5px 5px 15px rgba(0, 0, 0, 0.8)';
        clip.style.clipPath = getPreviewShadowClip(index, count);
      }
      clip.appendChild(image);
      if (lidText) clip.appendChild(createLidPreviewLabel(lidText, previewSize, displaySlot.colorName));
      return clip;
    }

    function createPreviewStack(display, previewSize) {
      var imageWidth = getPreviewImageWidth(previewSize);
      var stack = document.createElement('span');
      stack.className = 'tw:flex tw:flex-col tw:items-center tw:justify-center';
      stack.style.width = imageWidth;
      stack.style.transformOrigin = 'center center';
      if (previewSize === 'colorBody') stack.style.transform = 'rotate(90deg)';
      var previewSlots = getPreviewStackSlots(display.colors);
      previewSlots.forEach(function (slot, index) {
        stack.appendChild(createPreviewCaseImage(slot, imageWidth, previewSize, index, previewSlots.length));
      });
      return stack;
    }

    function ensurePrimaryBaseImage(isThumb) {
      var image = isThumb ? primaryThumbImage : primaryMedia;
      var parent = isThumb ? primaryThumb : primarySlide;
      var frame = isThumb ? thumbPreviewFrame : mainPreviewFrame;
      if (image || !parent) return image;
      image = document.createElement('img');
      image.setAttribute(isThumb ? 'data-primary-thumb-image' : 'data-primary-media', '');
      image.alt = '';
      image.loading = isThumb ? 'lazy' : 'eager';
      image.className = isThumb
        ? 'tw:absolute tw:inset-0 tw:h-full tw:w-full tw:object-contain'
        : 'tw:h-full tw:w-full tw:object-contain';
      parent.insertBefore(image, frame || null);
      if (isThumb) primaryThumbImage = image;
      else primaryMedia = image;
      return image;
    }

    function setPreviewBaseImages(showComposite) {
      var mainImage = ensurePrimaryBaseImage(false);
      var thumbImage = ensurePrimaryBaseImage(true);
      var mainSrc = showComposite ? galleryBackgroundSrc : initialPrimaryMediaSrc;
      var thumbSrc = showComposite
        ? (galleryBackgroundThumbSrc || galleryBackgroundSrc)
        : initialPrimaryThumbSrc;

      if (mainImage) {
        if (mainSrc) mainImage.setAttribute('src', mainSrc);
        else mainImage.removeAttribute('src');
        mainImage.alt = showComposite ? '' : initialPrimaryMediaAlt;
        mainImage.style.display = mainSrc ? '' : 'none';
      }
      if (thumbImage) {
        if (thumbSrc) thumbImage.setAttribute('src', thumbSrc);
        else thumbImage.removeAttribute('src');
        thumbImage.alt = '';
        thumbImage.style.display = thumbSrc ? '' : 'none';
      }
      if (primaryPlaceholder) primaryPlaceholder.classList.toggle('tw:hidden', showComposite);
    }

    function shiftGalleryIndexes(minimumIndex, delta) {
      qa('[data-gallery-slide]').forEach(function (slide) {
        var index = parseInt(slide.getAttribute('data-slide-index'), 10);
        if (isFinite(index) && index >= minimumIndex) slide.setAttribute('data-slide-index', String(index + delta));
      });
      qa('[data-gallery-thumb]').forEach(function (thumb) {
        var index = parseInt(thumb.getAttribute('data-slide-target'), 10);
        if (isFinite(index) && index >= minimumIndex) thumb.setAttribute('data-slide-target', String(index + delta));
      });
    }

    function addOriginalPrimaryGalleryItem() {
      if (!primarySlide || !primaryThumb || !initialPrimaryMediaSrc || q('[data-original-primary-gallery-slide]')) return;
      var previousIndex = activeGalleryIndex;
      shiftGalleryIndexes(1, 1);

      var slide = document.createElement('div');
      slide.setAttribute('data-gallery-slide', '');
      slide.setAttribute('data-original-primary-gallery-slide', '');
      slide.setAttribute('data-slide-index', '1');
      slide.setAttribute('data-slide-type', 'primary-original');
      slide.setAttribute('data-active', 'false');
      slide.setAttribute('aria-hidden', 'true');
      slide.setAttribute('inert', '');
      slide.setAttribute('role', 'img');
      slide.setAttribute('aria-label', initialPrimaryMediaAlt || initialPrimaryLabel);
      slide.className = primarySlide.className;

      var slideImage = document.createElement('img');
      slideImage.src = initialPrimaryMediaSrc;
      slideImage.alt = initialPrimaryMediaAlt;
      slideImage.loading = 'lazy';
      slideImage.className = 'tw:h-full tw:w-full tw:object-contain';
      slide.appendChild(slideImage);
      primarySlide.parentNode.insertBefore(slide, primarySlide.nextSibling);

      var thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.setAttribute('data-gallery-thumb', '');
      thumb.setAttribute('data-original-primary-gallery-thumb', '');
      thumb.setAttribute('data-slide-target', '1');
      thumb.setAttribute('data-active', 'false');
      thumb.setAttribute('aria-label', initialPrimaryMediaAlt || initialPrimaryLabel);
      thumb.className = primaryThumb.className;

      var thumbImage = document.createElement('img');
      thumbImage.src = initialPrimaryThumbSrc || initialPrimaryMediaSrc;
      thumbImage.alt = '';
      thumbImage.loading = 'lazy';
      thumbImage.className = 'tw:absolute tw:inset-0 tw:h-full tw:w-full tw:object-contain';
      thumb.appendChild(thumbImage);
      primaryThumb.parentNode.insertBefore(thumb, primaryThumb.nextSibling);

      setActiveGallerySlide(previousIndex > 0 ? previousIndex + 1 : 0);
    }

    function removeOriginalPrimaryGalleryItem() {
      var slide = q('[data-original-primary-gallery-slide]');
      var thumb = q('[data-original-primary-gallery-thumb]');
      if (!slide && !thumb) return;
      var previousIndex = activeGalleryIndex;
      if (slide) slide.remove();
      if (thumb) thumb.remove();
      shiftGalleryIndexes(2, -1);
      setActiveGallerySlide(previousIndex === 1 ? 0 : (previousIndex > 1 ? previousIndex - 1 : previousIndex));
    }

    function syncOriginalPrimaryGalleryItem(showComposite) {
      if (showComposite) addOriginalPrimaryGalleryItem();
      else removeOriginalPrimaryGalleryItem();
    }

    function mountPreviewStack(frame, stack) {
      if (!frame) return;
      frame.innerHTML = '';
      if (!stack) {
        frame.classList.add('tw:hidden');
        frame.setAttribute('aria-hidden', 'true');
        frame.style.display = 'none';
        return;
      }
      frame.classList.remove('tw:hidden');
      frame.setAttribute('aria-hidden', 'true');
      frame.style.position = 'absolute';
      frame.style.top = '50%';
      frame.style.left = '50%';
      frame.style.right = 'auto';
      frame.style.bottom = 'auto';
      frame.style.transform = 'translate(-50%, -50%)';
      frame.style.pointerEvents = 'none';
      frame.style.zIndex = '10';
      frame.style.display = 'flex';
      frame.style.alignItems = 'center';
      frame.style.justifyContent = 'center';
      frame.appendChild(stack);
    }

    function hasLivePreviewSelection(display) {
      return !!(display
        && display.count
        && display.size
        && display.confirmedSteps
        && display.confirmedSteps['1']
        && display.confirmedSteps['2']);
    }

    function isLivePreviewReady(display) {
      return !!(hasLivePreviewSelection(display)
        && display.colors
        && display.colors.length === display.count
        && display.colors.every(function (slot) { return !!slot.imageUrl; }));
    }

    function renderLivePreview(display) {
      var configured = hasLivePreviewSelection(display);
      var ready = isLivePreviewReady(display);
      if (primarySlide) primarySlide.setAttribute('aria-label', configured ? buildPreviewAltText(display) : initialPrimaryLabel);
      if (primaryThumb) primaryThumb.setAttribute('aria-label', configured ? buildPreviewAltText(display) : initialPrimaryLabel);
      var signature = JSON.stringify({
        configured: configured,
        ready: ready,
        count: display && display.count,
        size: display && display.size && display.size.key,
        narrow: !!(previewWidthQuery && previewWidthQuery.matches),
        colors: (display && display.colors || []).map(function (slot) {
          return [slot.role, slot.label, slot.colorName, slot.variantId, slot.imageUrl, slot.lidText];
        })
      });
      if (signature === previewRenderSignature) return;
      previewRenderSignature = signature;

      syncOriginalPrimaryGalleryItem(configured);
      setPreviewBaseImages(configured);
      mountPreviewStack(mainPreviewFrame, ready ? createPreviewStack(display, 'main') : null);
      mountPreviewStack(thumbPreviewFrame, ready ? createPreviewStack(display, 'thumb') : null);
    }

    function refreshLivePreviewWidth() {
      previewRenderSignature = '';
      updatePrice();
    }

    // ---- confirm-gate step machinery -------------------------------------

    function getStepEl(step) { return q('[data-step="' + step + '"]'); }

    function setStepLocked(step, locked) {
      var el = getStepEl(step);
      if (!el) return;
      el.setAttribute('data-locked', locked ? 'true' : 'false');
      var header = el.querySelector('[data-step-header="' + step + '"]');
      if (header) {
        header.disabled = !!locked;
        header.setAttribute('aria-disabled', locked ? 'true' : 'false');
        header.classList.toggle('tw:opacity-50', !!locked);
        header.classList.toggle('tw:cursor-not-allowed', !!locked);
      }
      applyStepVisual(step);
    }

    function setStepTouched(step, touched) {
      var el = getStepEl(step);
      if (!el) return;
      state.touched[step] = !!touched;
      el.setAttribute('data-touched', touched ? 'true' : 'false');
      applyStepVisual(step);
      var summary = q('[data-step-summary="' + step + '"]');
      if (summary) {
        var hasText = !!summary.getAttribute('data-summary-value');
        summary.classList.toggle('tw:hidden', !(hasText && touched));
      }
    }

    function setStepActive(step, active) {
      var el = getStepEl(step);
      if (!el) return;
      el.setAttribute('data-active', active ? 'true' : 'false');
      applyStepVisual(step);
    }

    function getStepVisual(el) {
      var active = el.getAttribute('data-active') === 'true';
      var touched = el.getAttribute('data-touched') === 'true';
      var locked = el.getAttribute('data-locked') === 'true';
      if (locked) return 'locked';
      if (active) return 'active';
      if (touched) return 'confirmed';
      return 'pending';
    }

    function applyStepVisual(step) {
      var el = getStepEl(step);
      if (!el) return;
      var s = getStepVisual(el);
      var active = s === 'active';
      var confirmed = s === 'confirmed';
      var locked = s === 'locked';
      var pending = s === 'pending';
      el.setAttribute('data-step-state', s);
      el.classList.toggle('tw:border-blue-300', active);
      el.classList.toggle('tw:border-green-300', confirmed);
      el.classList.toggle('tw:border-gray-300', pending || locked);
      el.classList.toggle('tw:ring-1', active || confirmed);
      el.classList.toggle('tw:ring-blue-300', active);
      el.classList.toggle('tw:ring-green-300', confirmed);
      el.classList.toggle('tw:shadow-sm', active || confirmed);
      var header = el.querySelector('[data-step-header="' + step + '"]');
      if (header) {
        header.setAttribute('aria-expanded', active ? 'true' : 'false');
        header.classList.toggle('tw:bg-blue-50', active);
        header.classList.toggle('tw:bg-green-50', confirmed);
        header.classList.toggle('tw:bg-gray-50', pending || locked);
        header.classList.toggle('tw:text-gray-800', active || confirmed);
        header.classList.toggle('tw:text-gray-700', pending || locked);
      }
      var chevron = el.querySelector('[data-step-chevron]');
      if (chevron) {
        chevron.classList.toggle('tw:rotate-180', active);
        chevron.classList.toggle('tw:text-blue-700', active);
        chevron.classList.toggle('tw:text-green-700', confirmed);
        chevron.classList.toggle('tw:text-gray-500', pending || locked);
      }
      var badge = el.querySelector('[data-step-badge]');
      if (badge) {
        badge.setAttribute('data-state', s);
        badge.classList.toggle('tw:text-gray-800', active || confirmed);
        badge.classList.toggle('tw:text-gray-700', pending || locked);
        var chk = badge.querySelector('[data-badge-check]');
        if (chk) {
          chk.classList.toggle('tw:hidden', !confirmed);
          chk.classList.toggle('tw:inline-flex', confirmed);
        }
      }
    }

    function setStepSummary(step, text) {
      var el = q('[data-step-summary="' + step + '"]');
      if (!el) return;
      var value = text || '';
      el.innerHTML = '';
      if (value) {
        var labelEl = document.createElement('span');
        labelEl.className = 'tw:font-normal tw:text-gray-600';
        labelEl.textContent = 'Selected: ';
        var valueEl = document.createElement('span');
        valueEl.className = 'tw:font-bold tw:text-black';
        valueEl.textContent = value;
        el.appendChild(labelEl);
        el.appendChild(valueEl);
        el.setAttribute('data-summary-value', value);
        el.setAttribute('aria-label', 'Selected: ' + value);
      } else {
        el.removeAttribute('data-summary-value');
        el.removeAttribute('aria-label');
      }
      var stepEl = getStepEl(step);
      var touched = stepEl && stepEl.getAttribute('data-touched') === 'true';
      el.classList.toggle('tw:hidden', !(value && touched));
    }

    function setStepSummaryContent(step, value, content) {
      var el = q('[data-step-summary="' + step + '"]');
      if (!el) return;
      var summaryValue = String(value || '').trim();
      el.innerHTML = '';
      if (summaryValue && content) {
        el.appendChild(content);
        el.setAttribute('data-summary-value', summaryValue);
        el.setAttribute('aria-label', 'Selected: ' + summaryValue);
      } else {
        el.removeAttribute('data-summary-value');
        el.removeAttribute('aria-label');
      }
      var stepEl = getStepEl(step);
      var touched = stepEl && stepEl.getAttribute('data-touched') === 'true';
      el.classList.toggle('tw:hidden', !(summaryValue && content && touched));
    }

    function setStepBodyVisible(step, visible) {
      var body = q('[data-step-body="' + step + '"]');
      if (!body) return;
      if (!visible && openColorPopup && body.contains(openColorPopup)) dismissOpenColorPopup();
      body.setAttribute('data-collapsed', visible ? 'false' : 'true');
      if (visible) body.removeAttribute('inert');
      else body.setAttribute('inert', '');
      var header = q('[data-step-header="' + step + '"]');
      if (header) header.setAttribute('aria-expanded', visible ? 'true' : 'false');
    }

    function collapseAll() {
      qa('[data-step]').forEach(function (el) {
        var n = el.getAttribute('data-step');
        setStepBodyVisible(n, false);
        setStepActive(n, false);
      });
    }

    function expandStep(step) {
      collapseAll();
      setStepBodyVisible(step, true);
      setStepActive(step, true);
      state.expandedStep = step;
      if (step === '2' && state.touched['1']) prepareSizeAvailability(false);
      if (step === '3') onStep3Expand();
      if (step === '4') renderLidStep();
      if (step === '5') renderPocketStep();
      if (step === '6') renderUltemStep();
    }

    function collapseStep(step) {
      setStepBodyVisible(step, false);
      setStepActive(step, false);
      if (state.expandedStep === step) state.expandedStep = '';
    }

    // Every confirmed downstream step is invalidated when an upstream choice changes.
    function resetStepsAfter(step) {
      var start = parseInt(step, 10) + 1;
      if (start <= 6) state.ultem = { mode: 'NONE' };
      ['1', '2', '3', '4', '5', '6'].forEach(function (n) {
        if (parseInt(n, 10) >= start) {
          setStepTouched(n, false);
          setStepSummary(n, '');
          setStepBodyVisible(n, false);
          setStepLocked(n, true);
        }
      });
    }

    // ---- Step 1: Case Count ----------------------------------------------

    function renderCountSelection() {
      qa('[data-count-option]').forEach(function (btn) {
        var n = parseInt(btn.getAttribute('data-count-option'), 10);
        var status = state.size ? getCountSizeAvailability(n, state.size) : null;
        var catalogUnavailable = !hasSellableTierVariantForCount(n);
        var selectionUnavailable = !!(status && status.ready && !status.available);
        var unavailable = catalogUnavailable || selectionUnavailable;
        var selected = n === state.count;
        var selectedVisual = selected && !unavailable;
        var sizeName = state.size && (state.size.displayName || state.size.label);
        var scope = catalogUnavailable ? 'catalog' : (selectionUnavailable ? 'selection' : '');
        var reason = catalogUnavailable
          ? 'catalog_count_unavailable'
          : (selectionUnavailable && status.reason ? status.reason : '');
        var visibleStatus = '';
        var detailedStatus = '';
        if (catalogUnavailable) {
          visibleStatus = 'Unavailable in every size';
          detailedStatus = 'This composition is unavailable in every size.';
        } else if (selectionUnavailable) {
          visibleStatus = status.reason === 'role_payload_error'
            ? 'Could not check ' + (sizeName || 'this size')
            : 'Unavailable for ' + (sizeName || 'this size');
          detailedStatus = status.message || visibleStatus;
        }

        // Keep unavailable cards focusable so keyboard and screen-reader users
        // can discover the visible reason; onCountClick enforces aria-disabled.
        btn.disabled = false;
        btn.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        btn.setAttribute('data-ui-state', unavailable ? 'unavailable' : (selected ? 'selected' : 'available'));
        btn.setAttribute('data-availability-scope', scope);
        btn.setAttribute('data-availability-reason', reason);
        btn.classList.toggle('tw:border-black', selectedVisual);
        btn.classList.toggle('tw:border-2', selectedVisual);
        btn.classList.toggle('tw:border-gray-300', !selectedVisual && !unavailable);
        btn.classList.toggle('tw:border-gray-200', unavailable);
        btn.classList.toggle('tw:bg-gray-50', selectedVisual);
        btn.classList.toggle('tw:bg-white', !selectedVisual && !unavailable);
        btn.classList.toggle('tw:bg-gray-100', unavailable);
        btn.classList.toggle('tw:text-gray-900', !unavailable);
        btn.classList.toggle('tw:text-gray-500', unavailable);
        btn.classList.toggle('tw:cursor-pointer', !unavailable);
        btn.classList.toggle('tw:cursor-not-allowed', unavailable);
        btn.title = detailedStatus;
        var chk = btn.querySelector('[data-count-check]');
        if (chk) chk.classList.toggle('tw:hidden', !selectedVisual);
        var statusEl = btn.querySelector('[data-count-status]');
        if (statusEl) {
          statusEl.textContent = visibleStatus;
          statusEl.classList.toggle('tw:hidden', !visibleStatus);
        }
      });
      var confirm1 = q('[data-step-confirm="1"]');
      if (confirm1) {
        var selectedStatus = state.size ? getCountSizeAvailability(state.count, state.size) : null;
        confirm1.disabled = state.count < 1
          || !hasSellableTierVariantForCount(state.count)
          || !!(selectedStatus && selectedStatus.ready && !selectedStatus.available);
      }
    }

    function onCountClick(btn) {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      var n = parseInt(btn.getAttribute('data-count-option'), 10);
      if (!n || n === state.count) return;
      availabilityToken += 1;
      availabilityLoading = false;
      state.count = n;
      state.size = null;
      state.colors = [];
      state.lid = { mode: null, values: blankValues(n) };
      state.pocket = { mode: null, startDay: 'SUN', singleLabel: '' };
      state.ultem = { mode: 'NONE' };
      if (state.touched['1']) {
        setStepTouched('1', false);
        setStepSummary('1', '');
      }
      resetStepsAfter('1');
      setSizeValidation('');
      renderCountSelection();
      renderSizeSelection();
      setActiveGallerySlide(0);
      refreshAtc();
    }

    // ---- Step 2: Case Size (derived from the tier product) ----------------

    function renderSizeOptions() {
      var container = q('[data-size-options]');
      if (!container) return;
      container.innerHTML = '';
      if (!sizeStep) {
        container.textContent = 'Could not display case sizes. Please refresh the page.';
        return;
      }
      sizes.forEach(function (sz) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('data-size-option', sz.key);
        btn.setAttribute('aria-pressed', 'false');
        btn.className = 'tw:border tw:bg-white tw:transition-all tw:duration-200 tw:ease-standard tw:focus-visible:ring-2 tw:focus-visible:ring-gray-950 tw:focus-visible:ring-offset-1 tw:focus:outline-none';
        container.appendChild(btn);
      });
      updateSizeOptionPresentation();
      renderSizeSelection();
    }

    function updateSizeOptionPresentation() {
      var container = q('[data-size-options]');
      if (!container || !sizeStep) return;
      if (sizeCapacityBar && sizeCapacityBar.parentNode) sizeCapacityBar.parentNode.removeChild(sizeCapacityBar);
      sizeCapacityBar = null;
      var shapes = { 1: 'v', 2: 'pair', 3: 'trio', 4: 'quad' };
      var models = sizes.map(function (size) {
        return { key: size.key, name: size.displayName || size.label || size.key,
          thumbnail: size.thumbnail, shape: shapes[state.count], counts: pillCounts[size.category] };
      });
      var presentation = state.count && models.length ? sizeStep.prepare(models)
        : { icons: false, rows: models.map(function (row) { return Object.assign({}, row, { svg: '', capacity: '' }); }) };
      if (presentation.icons) {
        sizeCapacityBar = sizeStep.createBar();
        container.parentNode.insertBefore(sizeCapacityBar, container);
      }
      qa('[data-size-option]').forEach(function (btn) {
        var key = btn.getAttribute('data-size-option');
        var index = sizes.findIndex(function (size) { return size.key === key; });
        if (index < 0) return;
        btn.innerHTML = '';
        sizeStep.appendRow(btn, presentation.rows[index], { selected: !!(state.size && state.size.key === key) });
        btn.querySelector('[data-size-thumbnail-wrap]').setAttribute('data-size-count', String(state.count));
        btn.querySelector('[data-size-badge]').setAttribute('data-size-status', '');
      });
    }

    function getSizeStatusLabel(status, pending) {
      if (pending) return 'Checking stock...';
      if (!status || status.available) return '';
      if (status.reason === 'tier_variant_unavailable') return 'Not offered for this count';
      if (status.reason === 'role_payload_error') return 'Availability check failed';
      if (status.reason === 'color_catalog_empty') return 'No colors are available for this selection.';
      if (status.reason === 'role_capacity') {
        var roleName = status.role
          ? status.role.charAt(0).toUpperCase() + status.role.slice(1).toLowerCase()
          : '';
        return roleName ? roleName + ' case stock unavailable' : 'Not enough case stock';
      }
      return 'Unavailable for this count';
    }

    function renderSizeSelection() {
      var tile = q('[data-size-thumbnail-wrap]');
      if (tile && tile.getAttribute('data-size-count') !== String(state.count)) updateSizeOptionPresentation();
      qa('[data-size-option]').forEach(function (btn) {
        var key = btn.getAttribute('data-size-option');
        var sz = sizes.filter(function (s) { return s.key === key; })[0];
        var status = state.count && sz ? getCountSizeAvailability(state.count, sz) : { ready: false, available: false };
        var pending = !!state.count && (availabilityLoading || !status.ready);
        var unavailable = !!state.count && status.ready && !status.available;
        var disabled = pending || unavailable;
        var selected = !!(state.size && state.size.key === key);
        var selectedVisual = selected && !disabled;
        var statusLabel = (pending || unavailable) ? getSizeStatusLabel(status, pending) : '';
        btn.disabled = false;
        btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
        btn.setAttribute('aria-busy', pending ? 'true' : 'false');
        btn.setAttribute('data-ui-state', pending ? 'loading' : (unavailable ? 'unavailable' : (selected ? 'selected' : 'available')));
        btn.setAttribute('data-availability-reason', unavailable && status.reason ? status.reason : '');
        btn.title = disabled ? (status.message || statusLabel) : '';
        btn.classList.toggle('tw:border-gray-950', selectedVisual);
        btn.classList.toggle('tw:ring-1', selectedVisual);
        btn.classList.toggle('tw:ring-gray-950', selectedVisual);
        btn.classList.toggle('tw:border-gray-300', !selectedVisual && !disabled);
        btn.classList.toggle('tw:border-gray-200', disabled);
        btn.classList.toggle('tw:bg-gray-50', selectedVisual || disabled);
        btn.classList.toggle('tw:bg-white', !selectedVisual && !disabled);
        btn.classList.toggle('tw:cursor-pointer', !disabled);
        btn.classList.toggle('tw:cursor-wait', pending);
        btn.classList.toggle('tw:cursor-not-allowed', unavailable);
        btn.classList.toggle('tw:hover:shadow-sm', !disabled);
        btn.classList.toggle('tw:hover:border-gray-400', !disabled);
        var chk = btn.querySelector('[data-size-check]');
        if (chk) chk.classList.toggle('tw:hidden', !selectedVisual);
        var thumbnailWrap = btn.querySelector('[data-size-thumbnail-wrap]');
        if (thumbnailWrap) thumbnailWrap.classList.toggle('tw:opacity-60', disabled);
        var badge = btn.querySelector('[data-size-badge]');
        if (badge) {
          var badgeLabel = statusLabel || (sz.bestSeller ? 'Best seller' : '');
          sizeStep.setBadge(btn, badge, badgeLabel, statusLabel ? 'status-dark' : 'best');
        }
      });
      var confirm2 = q('[data-step-confirm="2"]');
      if (confirm2) {
        var selectedStatus = state.size ? getCountSizeAvailability(state.count, state.size) : null;
        confirm2.disabled = !state.size || !selectedStatus || !selectedStatus.ready || !selectedStatus.available;
      }
    }

    function onSizeClick(btn) {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      var key = btn.getAttribute('data-size-option');
      var sz = sizes.filter(function (s) { return s.key === key; })[0];
      if (!sz || (state.size && state.size.key === key)) return;
      var status = getCountSizeAvailability(state.count, sz);
      if (!status.ready || !status.available) {
        setSizeValidation(status.message || 'This size is unavailable for the selected case count.');
        return;
      }
      state.size = sz;
      state.colors = [];
      state.ultem = { mode: 'NONE' };
      if (state.touched['2']) {
        setStepTouched('2', false);
        setStepSummary('2', '');
      }
      resetStepsAfter('2');
      setSizeValidation('');
      renderCountSelection();
      renderSizeSelection();
      setActiveGallerySlide(0);
      refreshAtc();
    }

    // ---- Step 3: per-slot Case Color ------------------------------------

    function setSizeValidation(msg, pending, retryable) {
      var el = q('[data-size-validation]');
      var retry = q('[data-size-retry]');
      if (!el) return;
      el.textContent = msg || '';
      el.setAttribute('data-ui-state', pending ? 'loading' : 'error');
      el.classList.toggle('tw:hidden', !msg);
      el.classList.toggle('tw:text-gray-600', !!pending);
      el.classList.toggle('tw:bg-gray-50', !!pending);
      el.classList.toggle('tw:border-gray-200', !!pending);
      el.classList.toggle('tw:text-red-800', !pending);
      el.classList.toggle('tw:bg-red-50', !pending);
      el.classList.toggle('tw:border-red-200', !pending);
      if (retry) retry.classList.toggle('tw:hidden', !retryable);
    }

    function fetchSizePresentation(force) {
      if (!sizeCardSourceHandle) return Promise.resolve(null);
      if (!force && sizePresentationPayload) return Promise.resolve(sizePresentationPayload);
      if (!force && sizePresentationPromise) return sizePresentationPromise;
      if (!window.theme || !theme.productDataFetcher || typeof theme.productDataFetcher.fetch !== 'function') {
        return Promise.resolve(null);
      }

      var sizeCardTwoSideListingContext = 'single-listing';
      var args = [
        'combined',
        sizeCardSourceHandle,
        'sizes',
        'Single Pack',
        listingContext,
        'ampm',
        roleHandles.left,
        roleHandles.right,
        sizeCardTwoSideListingContext
      ];
      if (force && typeof theme.productDataFetcher.invalidate === 'function') {
        theme.productDataFetcher.invalidate.apply(theme.productDataFetcher, args);
      }
      sizePresentationPromise = theme.productDataFetcher.fetch.apply(theme.productDataFetcher, args).then(function (payload) {
        sizePresentationPromise = null;
        if (
          payload
          && !payload.error
          && payload.family === 'ampm'
          && payload.listing_context === listingContext
          && payload.two_side_listing_context === sizeCardTwoSideListingContext
          && Array.isArray(payload.sizes)
        ) {
          sizePresentationPayload = payload;
          return payload;
        }
        console.warn('[magnetic-set] AMPM 3.0 size presentation could not be loaded:', payload);
        return null;
      }).catch(function (error) {
        sizePresentationPromise = null;
        console.warn('[magnetic-set] AMPM 3.0 size presentation fetch failed:', error);
        return null;
      });
      return sizePresentationPromise;
    }

    function fetchRolePayload(role, force) {
      var handle = roleHandles[role];
      if (!handle) { rolePayloads[role] = { error: 'missing_handle' }; return Promise.resolve(rolePayloads[role]); }
      if (!window.theme || !theme.productDataFetcher || typeof theme.productDataFetcher.fetch !== 'function') {
        rolePayloads[role] = { error: 'no_fetcher' };
        return Promise.resolve(rolePayloads[role]);
      }
      if (!force && Object.prototype.hasOwnProperty.call(rolePayloads, role)) {
        return Promise.resolve(rolePayloads[role]);
      }
      if (!force && rolePromises[role]) return rolePromises[role];
      if (force && typeof theme.productDataFetcher.invalidate === 'function') {
        theme.productDataFetcher.invalidate('magneticset', handle, role, listingContext);
      }
      rolePromises[role] = theme.productDataFetcher.fetch('magneticset', handle, role, listingContext).then(function (data) {
        if (
          data
          && !data.error
          && data.listing_context !== listingContext
        ) {
          console.warn('[magnetic-set] Listing context filtering was not applied to the ' + role + ' role payload.');
          rolePayloads[role] = { error: 'listing_context_mismatch', role: role };
        } else {
          rolePayloads[role] = data || { error: 'empty' };
        }
        delete rolePromises[role];
        return rolePayloads[role];
      }).catch(function (e) {
        rolePayloads[role] = { error: 'fetch_failed', message: String(e && e.message || e) };
        delete rolePromises[role];
        return rolePayloads[role];
      });
      return rolePromises[role];
    }

    function isRoleVariantSellable(v) {
      if (!v) return false;
      return v.available === true
        || !v.inventory_management
        || v.inventory_policy === 'continue';
    }

    function getEffectiveVariantCap(v) {
      if (!isRoleVariantSellable(v)) return 0;
      if (!v.inventory_management || v.inventory_policy === 'continue') return Infinity;
      // Shopify's sellable flag is authoritative for at least one unit. Some
      // storefront payloads omit/coerce inventory_quantity to 0 even while the
      // tracked variant remains available in the current market.
      var raw = getStorefrontInventoryFloor(v);
      var reserved = cartReservedByVariantId[String(v.id)] || 0;
      return Math.max(0, raw - reserved);
    }

    function roleVariantsForSize(role, size) {
      var payload = rolePayloads[role];
      if (!payload || payload.error || !size) return [];
      return (payload.variants || []).filter(function (v) {
        return (v.size_key && v.size_key === size.key)
          || (size.category && parseCategory(v.size_label) === size.category);
      });
    }

    function getRoleCapacity(role, size) {
      var seen = {};
      var total = 0;
      var variants = roleVariantsForSize(role, size);
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        var id = String(v.id || '');
        if (!id || seen[id]) continue;
        seen[id] = true;
        var cap = getEffectiveVariantCap(v);
        if (!isFinite(cap)) return Infinity;
        total += cap;
      }
      return total;
    }

    function getRoleDemand(count) {
      var demand = {};
      (ROLE_LAYOUT[count] || []).forEach(function (role) {
        demand[role] = (demand[role] || 0) + 1;
      });
      return demand;
    }

    function getCountSizeAvailability(count, size) {
      var tierVariant = findTierVariant(count, size && size.category);
      if (!isTierVariantSellable(tierVariant)) {
        return {
          ready: true,
          available: false,
          reason: 'tier_variant_unavailable',
          message: 'The Magnetic Set price variant is unavailable for this count and size.'
        };
      }
      if (!cartReservationsReady) return { ready: false, available: false, message: 'Checking stock...' };

      var demand = getRoleDemand(count);
      var roles = Object.keys(demand);
      for (var i = 0; i < roles.length; i++) {
        var role = roles[i];
        if (!Object.prototype.hasOwnProperty.call(rolePayloads, role)) {
          return { ready: false, available: false, message: 'Checking stock...' };
        }
        if (!rolePayloads[role] || rolePayloads[role].error) {
          return {
            ready: true,
            available: false,
            reason: 'role_payload_error',
            role: role,
            message: 'Case availability could not be loaded for this size.'
          };
        }
        if (!roleVariantsForSize(role, size).length) {
          return {
            ready: true,
            available: false,
            reason: 'color_catalog_empty',
            role: role,
            message: 'No colors are available for this selection.'
          };
        }
        var capacity = getRoleCapacity(role, size);
        if (capacity < demand[role]) {
          return {
            ready: true,
            available: false,
            reason: 'role_capacity',
            role: role,
            capacity: capacity,
            demand: demand[role],
            message: 'There is not enough ' + role.toUpperCase() + ' case stock for this count and size.'
          };
        }
      }
      return { ready: true, available: true, message: '' };
    }

    function parseCartCaseIds(value) {
      if (!value) return null;
      try {
        var parsed = typeof value === 'string' ? JSON.parse(value) : value;
        return parsed && Array.isArray(parsed.c) ? parsed.c : null;
      } catch (err) {
        return null;
      }
    }

    function addCartReservation(variantId, quantity) {
      var id = String(variantId || '');
      var qty = parseInt(quantity, 10) || 0;
      if (!id || qty < 1) return;
      cartReservedByVariantId[id] = (cartReservedByVariantId[id] || 0) + qty;
    }

    function setCartReservations(items) {
      cartReservedByVariantId = {};
      (items || []).forEach(function (item) {
        var quantity = parseInt(item.quantity, 10) || 0;
        var properties = item.properties || {};
        var caseIds = parseCartCaseIds(properties._magnetic_set);
        if (caseIds && caseIds.length) {
          caseIds.forEach(function (caseId) { addCartReservation(caseId, quantity); });
          return;
        }
        var marker = String(properties._ikg_bundle || '');
        if (marker.indexOf('mset2:') === 0 && marker.split(':').length > 2) return;
        addCartReservation(item.variant_id || item.id, quantity);
      });
      cartReservationsReady = true;
    }

    function loadCartItems() {
      return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (cart) {
          cartCurrency = normalizeCurrencyCode(cart && cart.currency);
          return (cart && cart.items) || [];
        });
    }

    function fetchCartReservations(force) {
      if (!force && cartReservationsPromise) return cartReservationsPromise;
      cartReservationsPromise = loadCartItems().then(function (items) {
        setCartReservations(items);
        return items;
      }).catch(function (err) {
        cartReservationsReady = true;
        console.warn('[magnetic-set] /cart.js fetch failed; cart-reserved subtraction skipped:', err);
        return [];
      });
      return cartReservationsPromise;
    }

    function invalidateSelectedSize(message) {
      availabilityToken += 1;
      fetchToken += 1;
      availabilityLoading = false;
      state.size = null;
      state.colors = [];
      setStepTouched('2', false);
      setStepSummary('2', '');
      resetStepsAfter('2');
      setStepLocked('2', false);
      renderCountSelection();
      renderSizeSelection();
      expandStep('2');
      setSizeValidation(message || 'That size is no longer available for the selected case count.');
      refreshAtc();
    }

    function prepareSizeAvailability(force) {
      if (!state.count) return Promise.resolve();
      var count = state.count;
      var needed = uniqueRoles(ROLE_LAYOUT[count] || []);
      var token = ++availabilityToken;
      availabilityLoading = true;
      setSizeValidation('Checking case stock...', true);
      renderSizeSelection();
      updatePrice();

      var requests = needed.map(function (role) { return fetchRolePayload(role, force); });
      requests.push(fetchSizePresentation(!!force));
      requests.push(fetchCartReservations(!!force));
      return Promise.all(requests).then(function () {
        if (token !== availabilityToken || count !== state.count) return;
        availabilityLoading = false;
        applySizePresentation(needed);
        renderCountSelection();
        renderSizeSelection();
        updatePrice();
        if (state.size) {
          var status = getCountSizeAvailability(state.count, state.size);
          if (status.ready && !status.available) {
            if (status.reason === 'color_catalog_empty') {
              state.colors = [];
              setStepTouched('2', false);
              setStepSummary('2', '');
              resetStepsAfter('2');
              setSizeValidation('No colors are available for this selection.');
              renderSizeSelection();
              refreshAtc();
              return;
            }
            invalidateSelectedSize(status.message);
            return;
          }
        }
        var hasRoleError = needed.some(function (role) {
          return !rolePayloads[role] || !!rolePayloads[role].error;
        });
        if (hasRoleError) {
          setSizeValidation('Case availability could not be loaded. Please try again.', false, true);
          updatePrice();
          return;
        }
        var anyAvailable = sizes.some(function (size) {
          var status = getCountSizeAvailability(state.count, size);
          return status.ready && status.available;
        });
        setSizeValidation(anyAvailable ? '' : 'No sizes currently have enough stock for this case count.');
        updatePrice();
      });
    }

    function onStep3Expand(force) {
      setColorValidation('');
      if (!state.count || !state.size) return;
      var needed = uniqueRoles(ROLE_LAYOUT[state.count] || []);
      setColorState('loading');
      var token = ++fetchToken;
      var requests = needed.map(function (role) { return fetchRolePayload(role, force); });
      requests.push(fetchCartReservations(!!force));
      Promise.all(requests).then(function () {
        if (token !== fetchToken) return; // superseded by a later count/size change
        var availability = getCountSizeAvailability(state.count, state.size);
        if (availability.ready && !availability.available) {
          setColorState('ready');
          if (availability.reason === 'color_catalog_empty') {
            state.colors = [];
            renderColorSlots();
            setColorValidation('No colors are available for this selection.');
            refreshAtc();
            return;
          }
          invalidateSelectedSize(availability.message);
          return;
        }
        var anyOk = needed.some(function (r) { var p = rolePayloads[r]; return p && !p.error; });
        if (!anyOk) {
          setColorState('error', 'We could not load colors for these cases right now.');
          updatePrice();
          return;
        }
        setColorState('ready');
        renderColorSlots();
        refreshAtc();
      });
    }

    function matchesSize(v) {
      if (!state.size) return false;
      if (v.size_key && v.size_key === state.size.key) return true;
      if (state.size.category && parseCategory(v.size_label) === state.size.category) return true;
      return false;
    }

    function getRoleColorUsage(role, colorKey, excludeSlotIdx) {
      var roles = ROLE_LAYOUT[state.count] || [];
      var usage = 0;
      for (var i = 0; i < state.colors.length; i++) {
        if (i === excludeSlotIdx || roles[i] !== role) continue;
        if (state.colors[i] && state.colors[i].colorKey === colorKey) usage += 1;
      }
      return usage;
    }

    function isColorUnavailableForSlot(role, variant, colorKey, slotIdx) {
      var cap = getEffectiveVariantCap(variant);
      var usageElsewhere = getRoleColorUsage(role, colorKey, slotIdx);
      return cap <= usageElsewhere;
    }

    function colorsForSlot(role, slotIdx) {
      var payload = rolePayloads[role];
      if (!payload || payload.error) return { error: (payload && payload.error) || 'no_data', colors: [] };
      var seen = {}, colors = [];
      roleVariantsForSize(role, state.size).forEach(function (v) {
        var ck = v.color_key || handleize(v.color_name);
        if (!ck || seen[ck]) return;
        seen[ck] = true;
        colors.push({
          colorKey: ck,
          colorName: v.color_name,
          variantId: String(v.id),
          variant: v,
          sortIndex: v.sort_index,
          unavailable: isColorUnavailableForSlot(role, v, ck, slotIdx),
          soldOut: !!v.sold_out,
          swatch: (v.case_color && v.case_color.swatch_image) || null,
          badges: (v.case_color && v.case_color.badges) || []
        });
      });
      return { error: null, colors: colors };
    }

    function ensureSlotDefault(slotIdx, colors) {
      var cur = state.colors[slotIdx];
      if (cur) {
        var match = colors.filter(function (c) { return c.colorKey === cur.colorKey; })[0];
        if (match && !match.unavailable) { cur.variantId = match.variantId; cur.colorName = match.colorName; return; }
      }
      var firstAvail = sortColorsByAvailability(colors).filter(function (c) { return !c.unavailable; })[0];
      state.colors[slotIdx] = firstAvail
        ? { colorKey: firstAvail.colorKey, colorName: firstAvail.colorName, variantId: firstAvail.variantId }
        : undefined;
    }

    function getColorPopupForTrigger(trigger) {
      if (!trigger) return null;
      var popupId = trigger.getAttribute('popovertarget');
      if (!popupId) return null;
      var popup = document.getElementById(popupId);
      if (!popup || !section.contains(popup)) return null;
      return popup;
    }

    function isColorPopupOpen(popup) {
      if (!popup || typeof popup.matches !== 'function') return false;
      try { return popup.matches(':popover-open'); }
      catch (e) { return false; }
    }

    function placeColorPopup(popup) {
      if (!popup) return;
      var trigger = popup.__colorTrigger;
      if (!trigger || !trigger.isConnected) return;
      var rect = trigger.getBoundingClientRect();
      var viewportPadding = 8;
      var viewportWidth = Math.max(0, window.innerWidth || document.documentElement.clientWidth || 0);
      var width = Math.max(0, Math.min(rect.width, viewportWidth - (viewportPadding * 2)));
      var left = Math.max(viewportPadding, Math.min(rect.left, viewportWidth - width - viewportPadding));
      popup.style.position = 'fixed';
      popup.style.margin = '0';
      popup.style.left = left + 'px';
      popup.style.right = 'auto';
      popup.style.bottom = 'auto';
      popup.style.width = width + 'px';
      var popupHeight = popup.offsetHeight || 0;
      var spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < popupHeight + 16 && rect.top > popupHeight + 16) {
        popup.style.top = (rect.top - popupHeight - 4) + 'px';
      } else {
        popup.style.top = (rect.bottom + 4) + 'px';
      }
    }

    function placeOpenColorPopup() {
      if (openColorPopup) placeColorPopup(openColorPopup);
    }

    function bindColorPopupPositionListeners() {
      if (colorPopupListenersBound) return;
      window.addEventListener('resize', placeOpenColorPopup);
      window.addEventListener('scroll', placeOpenColorPopup, true);
      colorPopupListenersBound = true;
    }

    function unbindColorPopupPositionListeners() {
      if (!colorPopupListenersBound) return;
      window.removeEventListener('resize', placeOpenColorPopup);
      window.removeEventListener('scroll', placeOpenColorPopup, true);
      colorPopupListenersBound = false;
    }

    function dismissOpenColorPopup() {
      var popup = openColorPopup;
      openColorPopup = null;
      unbindColorPopupPositionListeners();
      if (!popup) return;
      var trigger = popup.__colorTrigger;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (typeof popup.hidePopover === 'function' && isColorPopupOpen(popup)) {
        popup.hidePopover();
      }
    }

    function getEnabledColorOptions(popup) {
      if (!popup) return [];
      return Array.prototype.slice.call(popup.querySelectorAll('[data-color-option]')).filter(function (option) {
        return option.getAttribute('aria-disabled') !== 'true';
      });
    }

    function focusColorPopupOption(popup, focusLast) {
      var options = getEnabledColorOptions(popup);
      if (!options.length) return;
      var selected = options.filter(function (option) {
        return option.getAttribute('aria-selected') === 'true';
      })[0];
      var target = focusLast ? options[options.length - 1] : (selected || options[0]);
      if (target) target.focus();
    }

    function onColorPopupBeforeToggle(e) {
      if (e.newState === 'open') placeColorPopup(e.target);
    }

    function onColorPopupToggle(e) {
      var popup = e.target;
      var trigger = popup.__colorTrigger;
      if (e.newState === 'open') {
        openColorPopup = popup;
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
        placeColorPopup(popup);
        bindColorPopupPositionListeners();
        window.requestAnimationFrame(function () {
          if (openColorPopup !== popup) return;
          focusColorPopupOption(popup, popup.__focusLast === true);
          popup.__focusLast = false;
        });
      } else {
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (openColorPopup === popup) {
          openColorPopup = null;
          unbindColorPopupPositionListeners();
        }
      }
    }

    function prepareColorPopupFromTrigger(trigger) {
      var popup = getColorPopupForTrigger(trigger);
      if (popup) placeColorPopup(popup);
    }

    function renderColorSlots() {
      var container = q('[data-color-slots]');
      if (!container) return;
      dismissOpenColorPopup();
      container.innerHTML = '';
      var count = state.count;
      var roles = ROLE_LAYOUT[count] || [];
      var labels = SLOT_LABELS[count] || [];
      if (state.colors.length !== count) state.colors.length = count;
      for (var i = 0; i < count; i++) {
        container.appendChild(buildColorSlot(i, roles[i], labels[i] || ('Case ' + (i + 1))));
      }
      renderColorBodyPreview();
      updateColorConfirm();
    }

    function buildColorSlot(slotIdx, role, label) {
      var wrap = document.createElement('div');
      wrap.setAttribute('data-color-slot-wrap', slotIdx);
      wrap.setAttribute('data-color-role', role);
      wrap.setAttribute('role', 'listitem');
      wrap.className = 'tw:flex tw:w-full tw:flex-col tw:gap-2 tw:sm:max-w-md';
      var heading = document.createElement('div');
      heading.setAttribute('data-color-slot-label', '');
      heading.className = 'tw:text-xs tw:font-bold tw:leading-tight tw:text-black';
      heading.textContent = label;
      wrap.appendChild(heading);

      var res = colorsForSlot(role, slotIdx);
      if (res.error || !res.colors.length) {
        var msg = document.createElement('p');
        msg.className = 'tw:text-sm tw:text-gray-500 tw:m-0';
        if (res.error === 'not_found') {
          msg.textContent = 'This case is coming soon.';
        } else if (res.error) {
          msg.textContent = 'Colors are unavailable right now.';
        } else {
          msg.textContent = 'No colors are available for this selection.';
        }
        wrap.appendChild(msg);
        return wrap;
      }

      ensureSlotDefault(slotIdx, res.colors);

      var selectedState = state.colors[slotIdx];
      var selected = res.colors.filter(function (c) {
        return selectedState && c.colorKey === selectedState.colorKey;
      })[0];
      var popupId = 'magnetic-set-color-popup-' + sectionId + '-' + slotIdx;
      var headingId = popupId + '-label';
      var triggerNameId = popupId + '-value';
      heading.id = headingId;

      var trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.setAttribute('data-color-trigger', '');
      trigger.setAttribute('data-color-slot', String(slotIdx));
      trigger.setAttribute('popovertarget', popupId);
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-controls', popupId);
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-labelledby', headingId + ' ' + triggerNameId);
      trigger.setAttribute('data-ui-state', selected ? 'selected' : 'pending');
      trigger.className = [
        'tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-2',
        'tw:min-h-10 tw:rounded-md tw:border tw:border-gray-200 tw:bg-white tw:py-1.5 tw:pl-3 tw:pr-4 tw:text-left',
        'tw:cursor-pointer tw:transition-all tw:duration-fast tw:ease-standard tw:hover:border-gray-400',
        'tw:focus-visible:ring-2 tw:focus-visible:ring-black tw:focus-visible:ring-offset-1 tw:focus:outline-none'
      ].join(' ');

      var triggerInner = document.createElement('span');
      triggerInner.className = 'tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3';
      triggerInner.appendChild(buildColorSwatch(selected, 'tw:h-9 tw:w-9'));
      var triggerInfo = document.createElement('span');
      triggerInfo.className = 'tw:flex tw:min-w-0 tw:flex-1 tw:flex-wrap tw:items-center tw:gap-1.5';
      var triggerName = document.createElement('span');
      triggerName.id = triggerNameId;
      triggerName.setAttribute('data-color-trigger-name', '');
      triggerName.className = 'tw:min-w-0 tw:truncate tw:text-sm tw:font-semibold tw:text-gray-950';
      triggerName.textContent = selected ? selected.colorName : 'Choose a color';
      triggerInfo.appendChild(triggerName);
      var triggerBadges = buildColorBadges(selected && selected.badges);
      if (triggerBadges) triggerInfo.appendChild(triggerBadges);
      triggerInner.appendChild(triggerInfo);
      trigger.appendChild(triggerInner);

      var chevron = document.createElement('span');
      chevron.setAttribute('aria-hidden', 'true');
      chevron.className = 'tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:text-gray-500';
      chevron.innerHTML = '<svg class="tw:h-2 tw:w-3" viewBox="0 0 12 7" fill="none"><path d="M11 1L6 6L1 1" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      trigger.appendChild(chevron);
      wrap.appendChild(trigger);

      var popup = document.createElement('div');
      popup.id = popupId;
      popup.setAttribute('popover', 'auto');
      popup.setAttribute('data-color-popup', '');
      popup.setAttribute('data-color-slot', String(slotIdx));
      popup.setAttribute('role', 'listbox');
      popup.setAttribute('aria-labelledby', headingId);
      popup.className = 'tw:m-0 tw:p-0 tw:border-0 tw:bg-transparent';
      popup.__colorTrigger = trigger;

      var list = document.createElement('ul');
      list.setAttribute('role', 'none');
      list.className = 'tw:list-none tw:m-0 tw:p-0 tw:bg-white tw:rounded-lg tw:border tw:border-gray-200 tw:shadow-lg tw:overflow-hidden tw:max-h-72 tw:overflow-y-auto';
      sortColorsByAvailability(res.colors).forEach(function (c) {
        list.appendChild(buildColorOption(slotIdx, c));
      });
      popup.appendChild(list);
      popup.addEventListener('beforetoggle', onColorPopupBeforeToggle);
      popup.addEventListener('toggle', onColorPopupToggle);
      wrap.appendChild(popup);
      return wrap;
    }

    function getColorPreviewDisplay() {
      var display = buildDisplayState();
      if (!display || !display.count || !display.size || !display.colors || display.colors.length !== display.count) return null;
      return display.colors.every(function (slot) { return !!slot.imageUrl; }) ? display : null;
    }

    function renderColorBodyPreview() {
      var preview = q('[data-color-body-preview]');
      if (!preview) return;
      preview.innerHTML = '';
      var display = getColorPreviewDisplay();
      if (!display) {
        preview.classList.add('tw:hidden');
        preview.setAttribute('aria-hidden', 'true');
        preview.removeAttribute('role');
        preview.removeAttribute('aria-label');
        return;
      }
      preview.classList.remove('tw:hidden');
      preview.removeAttribute('aria-hidden');
      preview.setAttribute('role', 'img');
      preview.setAttribute('aria-label', buildPreviewAltText(display));
      preview.appendChild(createPreviewStack(display, 'colorBody'));
    }

    function renderColorStepSummary() {
      var summary = q('[data-step-summary="3"]');
      if (!summary) return;
      var display = getColorPreviewDisplay();
      var value = allSlotsChosen() ? colorSummaryText() : '';
      if (!display) {
        setStepSummary('3', value);
        return;
      }
      summary.innerHTML = '';
      if (display && value) {
        var wrap = document.createElement('span');
        wrap.setAttribute('data-color-header-preview', '');
        wrap.className = 'tw:flex tw:w-full tw:min-w-0 tw:items-center';
        wrap.appendChild(createPreviewStack(display, 'colorHeader'));
        summary.appendChild(wrap);
        summary.setAttribute('data-summary-value', value);
        summary.setAttribute('aria-label', 'Selected: ' + value);
      } else {
        summary.removeAttribute('data-summary-value');
        summary.removeAttribute('aria-label');
      }
      var stepEl = getStepEl('3');
      var touched = stepEl && stepEl.getAttribute('data-touched') === 'true';
      summary.classList.toggle('tw:hidden', !(display && value && touched));
    }

    function buildColorSwatch(c, sizeClasses) {
      var swatch = document.createElement('span');
      swatch.className = 'tw:relative tw:flex ' + sizeClasses + ' tw:flex-shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-full tw:border tw:border-gray-200 tw:bg-gray-100';
      if (c && c.swatch) {
        var img = document.createElement('img');
        img.src = c.swatch;
        img.alt = '';
        img.loading = 'lazy';
        img.className = 'tw:w-full tw:h-full tw:object-cover';
        swatch.appendChild(img);
      }
      if (c && c.soldOut) {
        var slash = document.createElement('span');
        slash.setAttribute('aria-hidden', 'true');
        slash.className = 'tw:pointer-events-none tw:absolute tw:left-1/2 tw:top-1/2 tw:h-0.5 tw:w-[140%] tw:-translate-x-1/2 tw:-translate-y-1/2 tw:-rotate-45 tw:bg-red-600';
        swatch.appendChild(slash);
      }
      return swatch;
    }

    function buildColorBadges(badges) {
      if (!badges || !badges.length) return null;
      var badgeWrap = document.createElement('span');
      badgeWrap.className = 'tw:flex tw:flex-wrap tw:gap-1';
      badges.forEach(function (b) {
        if (!b || !b.name) return;
        var badge = document.createElement('span');
        badge.className = 'tw:rounded-full tw:text-[10px] tw:font-medium tw:px-1.5 tw:py-0.5 tw:leading-tight tw:bg-gray-100 tw:text-gray-800';
        badge.textContent = b.name;
        if (b.color) { badge.style.background = b.color; badge.style.color = '#fff'; }
        badgeWrap.appendChild(badge);
      });
      return badgeWrap.childNodes.length ? badgeWrap : null;
    }

    function buildColorOption(slotIdx, c) {
      var option = document.createElement('li');
      option.setAttribute('role', 'option');
      option.setAttribute('tabindex', '-1');
      option.setAttribute('data-color-option', c.colorKey);
      option.setAttribute('data-color-slot', String(slotIdx));
      option.setAttribute('aria-disabled', c.unavailable ? 'true' : 'false');
      var selected = isSelected(slotIdx, c.colorKey);
      option.setAttribute('aria-selected', selected ? 'true' : 'false');
      var canNotify = c.soldOut && c.variant.bis !== false && !!notifyModalEl;
      option.setAttribute('aria-label', c.colorName + (c.soldOut ? ', sold out' : (c.unavailable ? ', no more available' : ''))
        + (canNotify ? ', notify me when available' : '') + (selected ? ', selected' : ''));
      option.setAttribute('data-ui-state', c.soldOut ? 'sold-out' : (c.unavailable ? 'unavailable' : (selected ? 'selected' : 'available')));
      if (canNotify) option.setAttribute('aria-haspopup', 'dialog');
      option.className = [
        'tw:flex tw:items-center tw:gap-2',
        'tw:min-h-10 tw:max-h-12 tw:px-3 tw:py-1.5',
        'tw:cursor-pointer tw:transition-colors tw:duration-fast tw:ease-standard tw:hover:bg-gray-50',
        'tw:focus-visible:ring-2 tw:focus-visible:ring-inset tw:focus-visible:ring-black tw:focus:outline-none',
        'tw:aria-disabled:cursor-not-allowed tw:aria-disabled:opacity-40 tw:aria-disabled:hover:bg-transparent',
        selected ? 'tw:bg-gray-50' : ''
      ].join(' ');
      option.appendChild(buildColorSwatch(c, 'tw:h-8 tw:w-8'));

      var info = document.createElement('span');
      info.className = 'tw:flex tw:min-w-0 tw:flex-1 tw:flex-wrap tw:items-center tw:gap-1.5';
      var name = document.createElement('span');
      name.className = 'tw:min-w-0 tw:truncate tw:text-sm tw:font-medium tw:text-gray-900';
      name.textContent = c.colorName;
      info.appendChild(name);
      var badgeWrap = buildColorBadges(c.badges);
      if (badgeWrap) info.appendChild(badgeWrap);
      if (c.unavailable) {
        var so = document.createElement('span');
        so.className = 'tw:rounded-full tw:bg-gray-100 tw:px-1.5 tw:py-0.5 tw:text-[10px] tw:font-semibold tw:leading-tight tw:text-gray-600';
        so.textContent = c.soldOut ? 'Sold out' : 'No more available';
        info.appendChild(so);
      }
      option.appendChild(info);

      var check = document.createElement('span');
      check.setAttribute('data-color-option-check', '');
      check.setAttribute('aria-hidden', 'true');
      check.className = (selected ? '' : 'tw:hidden ') + 'tw:flex tw:h-4 tw:w-4 tw:flex-shrink-0 tw:items-center tw:justify-center tw:text-black';
      check.innerHTML = '<svg class="tw:h-4 tw:w-4" viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 11.4 2.6 7.5 4 6.1l2.5 2.5L12 3.1l1.4 1.4z"/></svg>';
      option.appendChild(check);
      return option;
    }

    function isSelected(slotIdx, colorKey) {
      var sel = state.colors[slotIdx];
      return !!(sel && sel.colorKey === colorKey);
    }

    function onColorClick(btn) {
      var slotIdx = parseInt(btn.getAttribute('data-color-slot'), 10);
      var colorKey = btn.getAttribute('data-color-option');
      if (isNaN(slotIdx)) return;
      var role = (ROLE_LAYOUT[state.count] || [])[slotIdx];
      if (!role) return;
      var res = colorsForSlot(role, slotIdx);
      var entry = res.colors.filter(function (c) { return c.colorKey === colorKey; })[0];
      if (!entry) return;
      if (btn.getAttribute('aria-disabled') === 'true' || entry.unavailable) {
        if (!notifyModalEl || !entry.variant.sold_out || entry.variant.bis === false) return;
        var colorPopup = btn.closest('[data-color-popup]');
        var colorTrigger = colorPopup && colorPopup.__colorTrigger;
        dismissOpenColorPopup();
        openNotifyModal({
          count: state.count,
          size: state.size && state.size.key,
          role: role,
          slotLabel: (SLOT_LABELS[state.count] || [])[slotIdx] || getNotifyRoleLabel(role, state.count),
          color: entry.colorName,
          returnFocus: colorTrigger || btn
        });
        return;
      }
      if (isSelected(slotIdx, entry.colorKey)) {
        dismissOpenColorPopup();
        var currentTrigger = q('[data-color-trigger][data-color-slot="' + slotIdx + '"]');
        if (currentTrigger) currentTrigger.focus();
        return;
      }
      if (state.touched['3']) {
        setStepTouched('3', false);
        setStepSummary('3', '');
      }
      if (state.touched['4'] || state.touched['5'] || state.touched['6']) resetStepsAfter('3');
      state.colors[slotIdx] = { colorKey: entry.colorKey, colorName: entry.colorName, variantId: entry.variantId };
      renderColorSlots();
      setColorValidation('');
      updateColorConfirm();
      setActiveGallerySlide(0);
      var trigger = q('[data-color-trigger][data-color-slot="' + slotIdx + '"]');
      if (trigger) window.requestAnimationFrame(function () { trigger.focus(); });
      refreshAtc();
    }

    function onColorKeydown(e) {
      var target = e.target;
      if (!target || typeof target.closest !== 'function') return;
      var option = target.closest('[data-color-option]');
      if (option && section.contains(option)) {
        var popup = option.closest('[data-color-popup]');
        if (e.key === 'Escape') {
          e.preventDefault();
          var trigger = popup && popup.__colorTrigger;
          dismissOpenColorPopup();
          if (trigger) trigger.focus();
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onColorClick(option);
          return;
        }
        var options = getEnabledColorOptions(popup);
        var currentIndex = options.indexOf(option);
        var nextIndex = currentIndex;
        if (e.key === 'ArrowDown') nextIndex = Math.min(options.length - 1, currentIndex + 1);
        else if (e.key === 'ArrowUp') nextIndex = Math.max(0, currentIndex - 1);
        else if (e.key === 'Home') nextIndex = 0;
        else if (e.key === 'End') nextIndex = options.length - 1;
        else return;
        e.preventDefault();
        if (options[nextIndex]) options[nextIndex].focus();
        return;
      }

      var trigger = target.closest('[data-color-trigger]');
      if (!trigger || !section.contains(trigger)) return;
      if (e.key === 'Enter' || e.key === ' ') {
        prepareColorPopupFromTrigger(trigger);
        return;
      }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      var triggerPopup = getColorPopupForTrigger(trigger);
      if (!triggerPopup || typeof triggerPopup.showPopover !== 'function') return;
      e.preventDefault();
      triggerPopup.__focusLast = e.key === 'ArrowUp';
      placeColorPopup(triggerPopup);
      if (!isColorPopupOpen(triggerPopup)) triggerPopup.showPopover();
      else {
        focusColorPopupOption(triggerPopup, triggerPopup.__focusLast);
        triggerPopup.__focusLast = false;
      }
    }

    function allSlotsChosen() {
      if (!state.count) return false;
      for (var i = 0; i < state.count; i++) {
        if (!state.colors[i] || !state.colors[i].variantId) return false;
      }
      return true;
    }

    function isSelectedAllocationAvailable() {
      if (!allSlotsChosen() || !state.size) return false;
      var roles = ROLE_LAYOUT[state.count] || [];
      var usage = {};
      var variants = {};
      for (var i = 0; i < state.count; i++) {
        var selected = state.colors[i];
        var role = roles[i];
        if (!selected || !role) return false;
        var key = role + '::' + selected.colorKey;
        usage[key] = (usage[key] || 0) + 1;
        if (!variants[key]) {
          var matches = roleVariantsForSize(role, state.size).filter(function (v) {
            var colorKey = v.color_key || handleize(v.color_name);
            return colorKey === selected.colorKey && String(v.id) === String(selected.variantId);
          });
          variants[key] = matches[0] || null;
        }
      }
      return Object.keys(usage).every(function (key) {
        return getEffectiveVariantCap(variants[key]) >= usage[key];
      });
    }

    function colorSummaryText() {
      var names = [];
      for (var i = 0; i < state.count; i++) names.push(state.colors[i] ? state.colors[i].colorName : '-');
      return names.join(', ');
    }

    function updateColorConfirm() {
      var confirm3 = q('[data-step-confirm="3"]');
      if (confirm3) confirm3.disabled = !allSlotsChosen() || !isSelectedAllocationAvailable();
    }

    function setColorState(st, message) {
      var loading = q('[data-color-loading]');
      var error = q('[data-color-error]');
      var editor = q('[data-color-editor]');
      var slots = q('[data-color-slots]');
      var confirm3 = q('[data-step-confirm="3"]');
      var step = getStepEl('3');
      if (step) step.setAttribute('data-content-state', st);
      if (loading) loading.classList.toggle('tw:hidden', st !== 'loading');
      if (error) error.classList.toggle('tw:hidden', st !== 'error');
      if (editor) editor.classList.toggle('tw:hidden', st !== 'ready');
      if (slots) slots.classList.toggle('tw:hidden', st !== 'ready');
      if (confirm3) confirm3.classList.toggle('tw:hidden', st !== 'ready');
      if (st === 'error') {
        var emsg = q('[data-color-error-message]');
        if (emsg) emsg.textContent = message || 'Could not load colors. Please try again.';
      }
    }

    function setColorValidation(msg) {
      var el = q('[data-color-validation]');
      if (!el) return;
      el.textContent = msg || '';
      el.classList.toggle('tw:hidden', !msg);
    }

    // ---- Step 4: per-slot Lid Engraving ---------------------------------

    function applyModeButton(btn, selected, unavailable) {
      btn.disabled = !!unavailable;
      btn.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
      btn.removeAttribute('aria-selected');
      btn.setAttribute('data-ui-state', unavailable ? 'unavailable' : (selected ? 'selected' : 'available'));
      btn.classList.toggle('tw:border-gray-950', selected);
      btn.classList.toggle('tw:bg-gray-950', selected);
      btn.classList.toggle('tw:text-white', selected);
      btn.classList.toggle('tw:border-gray-300', !selected);
      btn.classList.toggle('tw:bg-white', !selected);
      btn.classList.toggle('tw:text-gray-900', !selected);
      btn.classList.toggle('tw:opacity-40', !!unavailable);
      btn.classList.toggle('tw:cursor-not-allowed', !!unavailable);
      btn.querySelectorAll('[data-mode-copy]').forEach(function (copy) {
        copy.classList.toggle('tw:text-white', selected);
        copy.classList.toggle('tw:text-gray-500', !selected && copy.hasAttribute('data-mode-secondary'));
      });
    }

    function setLidValidation(msg) {
      var el = q('[data-lid-validation]');
      if (!el) return;
      el.textContent = msg || '';
      el.classList.toggle('tw:hidden', !msg);
    }

    function updateLidFieldMeta(index, input) {
      var value = input ? String(input.value || '') : String((state.lid.values || [])[index] || '');
      var count = q('[data-lid-count="' + index + '"]');
      var atLimit = value.length >= lidMaxLength;
      if (count) {
        count.textContent = String(value.length) + '/' + lidMaxLength;
        count.classList.toggle('tw:text-gray-900', atLimit);
        count.classList.toggle('tw:text-gray-500', !atLimit);
      }
    }

    function getLidPreviewImageWidth(context) {
      var isPill = state.size && state.size.category === 'pill';
      if (context === 'body') return isPill ? '225px' : '255px';
      return isPill ? '200px' : '220px';
    }

    function getLidPreviewFontSize(context, value) {
      var isPill = state.size && state.size.category === 'pill';
      var fontSize;
      if (context === 'body') fontSize = isPill ? 16 : 22;
      if (context === 'main') {
        fontSize = previewWidthQuery && previewWidthQuery.matches
          ? (isPill ? 20 : 28)
          : (isPill ? 28 : 38);
      }
      if (context === 'thumb') fontSize = previewWidthQuery && previewWidthQuery.matches ? 4 : 8;
      if (!fontSize) fontSize = isPill ? 14 : 20;

      var textLength = String(value || '').trim().length;
      return textLength > 10 ? Math.max(4, Math.floor(fontSize * 10 / textLength)) : fontSize;
    }

    function createLidPreviewLabel(value, context, colorName) {
      var label = document.createElement('span');
      var style = getLidPreviewLabelStyle(colorName, context);
      label.setAttribute('aria-hidden', 'true');
      label.className = 'tw:absolute tw:left-1/2 tw:top-1/2 tw:flex tw:h-1/3 tw:w-[96%] tw:-translate-x-1/2 tw:-translate-y-1/2 tw:items-center tw:justify-center tw:text-center tw:leading-none tw:text-neutral-200';
      label.style.fontSize = getLidPreviewFontSize(context, value) + 'px';
      label.style.whiteSpace = 'nowrap';
      label.style.overflow = 'visible';
      label.style.color = style.color;
      if (style.textStroke) label.style.setProperty('-webkit-text-stroke', style.textStroke);
      label.textContent = value || '';
      return label;
    }

    function createLidPreviewFrame(context, values) {
      var imageWidth = getLidPreviewImageWidth(context);
      var roles = ROLE_LAYOUT[state.count] || [];
      var labels = SLOT_LABELS[state.count] || [];
      var descriptions = [];
      var slots = roles.map(function (role, index) {
        var variant = findPreviewVariant(role, index);
        return {
          slotIndex: index,
          role: role,
          label: labels[index] || ('Case ' + (index + 1)),
          value: values[index] || '',
          colorName: state.colors[index] && state.colors[index].colorName,
          imageUrl: getPreviewCaseSrc(variant, role)
        };
      });

      var frame = document.createElement('span');
      var stack = document.createElement('span');
      frame.setAttribute('data-lid-preview-frame', '');
      frame.setAttribute('role', 'img');
      frame.className = 'tw:relative tw:inline-flex tw:flex-shrink-0 tw:items-center tw:justify-center';
      frame.style.width = imageWidth;
      frame.style.overflow = 'visible';
      stack.className = 'tw:flex tw:flex-col tw:items-center tw:justify-center';
      stack.style.width = imageWidth;

      getPreviewStackSlots(slots).forEach(function (slot) {
        descriptions.push(slot.label + ': ' + (slot.value || 'no engraving'));
        var layer = document.createElement('span');
        layer.className = 'tw:relative tw:block';
        layer.style.width = imageWidth;
        layer.style.lineHeight = '0';
        if (slot.imageUrl) {
          var image = document.createElement('img');
          image.src = slot.imageUrl;
          image.alt = '';
          image.loading = 'lazy';
          image.className = 'tw:block tw:h-auto tw:max-w-none tw:object-contain';
          image.style.width = imageWidth;
          image.style.height = 'auto';
          image.style.maxWidth = 'none';
          layer.appendChild(image);
        } else {
          var fallback = document.createElement('span');
          fallback.className = 'tw:flex tw:h-14 tw:w-full tw:items-center tw:justify-center tw:rounded-lg tw:border tw:border-gray-200 tw:bg-gray-100 tw:text-xs tw:text-gray-500';
          fallback.textContent = slot.colorName || slot.label;
          layer.appendChild(fallback);
        }
        layer.appendChild(createLidPreviewLabel(slot.value, context, slot.colorName));
        stack.appendChild(layer);
      });
      frame.setAttribute('aria-label', 'Lid engraving preview. ' + descriptions.join('. ') + '.');
      frame.appendChild(stack);
      return frame;
    }

    function renderLidBodyPreview() {
      var preview = q('[data-lid-body-preview]');
      if (!preview) return;
      preview.innerHTML = '';
      preview.appendChild(createLidPreviewFrame('body', normalizedLidValues()));
    }

    function renderLidStepSummary() {
      var values = state.lid.mode === 'CUSTOM' ? normalizedLidValues() : blankValues(state.count);
      var summaryText = values.filter(Boolean).join('|') || 'No lid engraving';
      var wrap = document.createElement('span');
      wrap.className = 'tw:flex tw:w-full tw:min-w-0 tw:flex-wrap tw:items-center tw:gap-1.5';
      wrap.appendChild(createLidPreviewFrame('header', values));
      setStepSummaryContent('4', summaryText, wrap);
    }

    function renderLidButtonPrice(variant) {
      var lidDisplay = buildDisplayAddons().detail.lid;
      qa('[data-lid-price]').forEach(function (el) {
        el.textContent = variant && lidDisplay.currentCents > 0
          ? '(+' + formatMoney(lidDisplay.currentCents) + ')'
          : '';
      });
    }

    function renderLidFields() {
      var container = q('[data-lid-fields]');
      if (!container) return;
      container.innerHTML = '';
      var values = normalizedLidValues();
      state.lid.values = values;
      var labels = SLOT_LABELS[state.count] || [];
      for (var i = 0; i < state.count; i++) {
        var row = document.createElement('label');
        row.className = 'tw:flex tw:min-w-0 tw:flex-col tw:mb-0';

        var content = document.createElement('span');
        content.className = 'tw:flex tw:min-w-0 tw:flex-col tw:gap-1';

        var labelText = document.createElement('span');
        labelText.className = 'tw:text-xs tw:font-bold tw:leading-tight tw:text-black';
        labelText.textContent = labels[i] || ('Case ' + (i + 1));
        content.appendChild(labelText);

        var wrap = document.createElement('span');
        wrap.className = 'tw:relative tw:block';

        var input = document.createElement('input');
        input.type = 'text';
        input.maxLength = lidMaxLength;
        input.value = values[i] || '';
        input.placeholder = 'Optional — leave blank to skip';
        input.setAttribute('data-lid-input', String(i));
        input.setAttribute('data-lid-index', String(i));
        var countId = 'magnetic-set-lid-count-' + sectionId + '-' + i;
        input.setAttribute('aria-describedby', countId + ' magnetic-set-lid-validation-' + sectionId);
        input.className = 'tw:m-0 tw:min-h-[38px] tw:w-full tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:pl-3 tw:pr-12 tw:text-xs tw:transition-colors tw:duration-200 tw:ease-standard tw:hover:border-gray-400 tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-black tw:focus-visible:ring-offset-1';
        wrap.appendChild(input);

        var count = document.createElement('span');
        count.id = countId;
        count.setAttribute('data-lid-count', String(i));
        count.className = 'tw:pointer-events-none tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-xs tw:tabular-nums tw:text-gray-500';
        wrap.appendChild(count);

        content.appendChild(wrap);
        row.appendChild(content);
        container.appendChild(row);
        updateLidFieldMeta(i, input);
      }
    }

    function updateLidConfirm() {
      var confirm = q('[data-step-confirm="4"]');
      if (!confirm) return;
      var variant = findAddonVariant(lidProduct, state.count);
      confirm.disabled = state.lid.mode !== 'NONE'
        && !(state.lid.mode === 'CUSTOM' && !!variant && selectedLidCount() > 0);
    }

    function renderLidStep() {
      var variant = findAddonVariant(lidProduct, state.count);
      qa('[data-lid-mode]').forEach(function (btn) {
        var mode = btn.getAttribute('data-lid-mode');
        applyModeButton(btn, state.lid.mode === mode, mode === 'CUSTOM' && !variant);
      });
      renderLidButtonPrice(variant);
      var helpPrice = q('[data-lid-help-price]');
      if (helpPrice) helpPrice.textContent = variant
        ? formatMoney(priceCents(variant)) + ' per engraved lid'
        : 'only when engraving is available';

      var status = q('[data-lid-status]');
      if (status) {
        var message = !lidProduct
          ? 'Lid engraving is not configured. You can still skip this step.'
          : (!variant ? 'Lid engraving is unavailable for this case count. You can still skip this step.' : '');
        status.textContent = message;
        status.classList.toggle('tw:hidden', !message);
      }

      var editor = q('[data-lid-editor]');
      if (state.lid.mode === 'CUSTOM') renderLidFields();
      if (editor) editor.classList.toggle('tw:hidden', state.lid.mode !== 'CUSTOM');
      renderLidBodyPreview();
      setLidValidation('');
      updateLidConfirm();
      updatePrice();
    }

    function onLidModeClick(btn) {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      var mode = btn.getAttribute('data-lid-mode');
      if (mode !== 'CUSTOM' && mode !== 'NONE') return;
      if (state.lid.mode === mode) return;
      if (state.touched['4']) {
        setStepTouched('4', false);
        setStepSummary('4', '');
      }
      resetStepsAfter('4');
      state.lid.mode = mode;
      if (mode === 'NONE') state.lid.values = blankValues(state.count);
      else if (state.lid.values.length !== state.count) state.lid.values = blankValues(state.count);
      renderLidStep();
      refreshAtc();
    }

    function onLidInput(input) {
      var index = parseInt(input.getAttribute('data-lid-input'), 10);
      if (isNaN(index) || index < 0 || index >= state.count) return;
      if (state.touched['4']) {
        setStepTouched('4', false);
        setStepSummary('4', '');
        resetStepsAfter('4');
      }
      state.lid.values[index] = input.value.slice(0, lidMaxLength);
      updateLidFieldMeta(index, input);
      renderLidBodyPreview();
      renderLidButtonPrice(findAddonVariant(lidProduct, state.count));
      setLidValidation('');
      updateLidConfirm();
      refreshAtc();
    }

    // ---- Step 5: In-Pocket Engraving ------------------------------------

    function setPocketValidation(msg) {
      var el = q('[data-pocket-validation]');
      if (!el) return;
      el.textContent = msg || '';
      el.classList.toggle('tw:hidden', !msg);
    }

    function renderSinglePocketLabelOptions() {
      var select = q('[data-pocket-single-label]');
      if (!select) return;
      if (select.getAttribute('data-options-ready') !== 'true') {
        SINGLE_POCKET_LABEL_GROUPS.forEach(function (group) {
          var optgroup = document.createElement('optgroup');
          optgroup.label = group.label;
          group.values.forEach(function (value) {
            var option = document.createElement('option');
            option.value = value;
            option.textContent = value;
            optgroup.appendChild(option);
          });
          select.appendChild(optgroup);
        });
        select.setAttribute('data-options-ready', 'true');
      }
      select.value = isAllowedSinglePocketLabel(state.pocket.singleLabel)
        ? state.pocket.singleLabel
        : '';
    }

    function getPocketPreviewRows() {
      return buildPocketPreviewRows(
        state.count,
        state.pocket.mode,
        state.pocket.singleLabel,
        state.pocket.startDay
      );
    }

    function renderPocketButtonPrice(variant) {
      qa('[data-pocket-price]').forEach(function (el) {
        el.textContent = variant ? '(+' + formatMoney(variant.price) + ')' : '';
      });
    }

    function getPocketPreviewVariant(index) {
      var role = (ROLE_LAYOUT[state.count] || [])[index];
      return role ? findPreviewVariant(role, index) : null;
    }

    function getPocketPreviewColorKey(index) {
      var selected = state.colors[index];
      var variant = getPocketPreviewVariant(index);
      return handleize(
        (selected && selected.colorKey)
        || (variant && (variant.color_key || variant.color_name))
        || ''
      );
    }

    function getPocketBodyPreviewSrc(index) {
      var row = pocketImagesByColor[getPocketPreviewColorKey(index)];
      return row && row.weekly ? row.weekly : '';
    }

    function getPocketSummaryBackgroundSrc(index) {
      var variant = getPocketPreviewVariant(index);
      var role = (ROLE_LAYOUT[state.count] || [])[index];
      return getPreviewCaseSrc(variant, role);
    }

    function getPocketPreviewAreas() {
      return [6.5, 18.5, 31, 44, 56.5, 69, 81.5].map(function (top) {
        return { top: top + '%', left: '50%', width: '34px' };
      });
    }

    function createPocketBodyPreviewLabel(primaryText, secondaryText, area) {
      var primary = String(primaryText || '').trim().toUpperCase();
      var secondary = String(secondaryText || '').trim().toUpperCase();
      var label = document.createElement('span');
      label.setAttribute('aria-hidden', 'true');
      label.className = secondary
        ? 'tw:absolute tw:flex tw:flex-col tw:items-center tw:justify-center tw:text-center tw:leading-none tw:text-neutral-200'
        : 'tw:absolute tw:flex tw:items-center tw:justify-center tw:truncate tw:text-center tw:leading-none tw:text-neutral-200';
      label.style.left = area.left;
      label.style.top = area.top;
      label.style.transform = 'translate(-50%, -50%)';
      label.style.width = area.width;
      label.style.maxWidth = area.width;
      label.style.whiteSpace = 'nowrap';
      label.style.fontSize = '12px';

      if (secondary) {
        var primaryLine = document.createElement('span');
        var secondaryLine = document.createElement('span');
        label.style.gap = '1px';
        label.style.overflow = 'visible';
        primaryLine.style.display = 'block';
        primaryLine.style.fontSize = '12px';
        primaryLine.style.lineHeight = '1';
        primaryLine.textContent = primary;
        secondaryLine.style.display = 'block';
        secondaryLine.style.fontSize = '6px';
        secondaryLine.style.lineHeight = '1';
        secondaryLine.textContent = secondary;
        label.appendChild(primaryLine);
        label.appendChild(secondaryLine);
      } else {
        label.textContent = primary;
      }

      return label;
    }

    function createPocketBodyPreviewFrame(row) {
      var frame = document.createElement('span');
      var src = getPocketBodyPreviewSrc(row.index);
      var ariaValues = row.weekdays.map(function (day) {
        return day + (row.pocketLabel ? ' ' + row.pocketLabel : '');
      });
      frame.setAttribute('data-pocket-body-preview-frame', '');
      frame.setAttribute('role', 'img');
      frame.setAttribute('aria-label', row.slotLabel + (ariaValues.length ? ' pocket engraving preview: ' + ariaValues.join(', ') : ' pocket engraving preview'));
      frame.className = 'tw:relative tw:inline-flex tw:flex-shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-sm tw:bg-white';
      frame.style.width = '42px';
      frame.style.maxWidth = '100%';

      if (src) {
        var image = document.createElement('img');
        image.src = src;
        image.alt = '';
        image.loading = 'lazy';
        image.className = 'tw:block tw:h-auto tw:w-full tw:object-contain';
        frame.appendChild(image);
      } else {
        frame.style.aspectRatio = '1 / 1';
      }

      var areas = getPocketPreviewAreas();
      row.weekdays.forEach(function (day, index) {
        frame.appendChild(createPocketBodyPreviewLabel(day, row.pocketLabel, areas[index]));
      });
      return frame;
    }

    function renderPocketPreview() {
      var preview = q('[data-pocket-body-preview]');
      if (!preview) return;
      preview.innerHTML = '';
      var wrap = document.createElement('span');
      wrap.className = 'tw:inline-flex tw:max-w-full tw:flex-shrink-0 tw:items-center tw:justify-center';
      getPocketPreviewRows().forEach(function (row) {
        wrap.appendChild(createPocketBodyPreviewFrame(row));
      });
      preview.appendChild(wrap);
    }

    function applyPocketSummaryBackground(el, index) {
      var src = getPocketSummaryBackgroundSrc(index);
      if (!src) return;
      var safeSrc = encodeURI(String(src)).replace(/"/g, '%22');
      el.style.backgroundImage = 'url("' + safeSrc + '")';
      el.style.backgroundSize = 'cover';
      el.style.backgroundPosition = 'center';
      el.style.color = '#fff';
    }

    function createPocketSummaryPill(text, rowIndex) {
      var labelText = text && typeof text === 'object'
        ? { primary: String(text.primary || '').trim(), secondary: String(text.secondary || '').trim().toUpperCase() }
        : { primary: String(text || '').trim(), secondary: '' };
      var hasSecondaryText = !!labelText.secondary;
      var pill = document.createElement('span');
      pill.setAttribute('data-magnetic-pocket-summary-pill', '');
      pill.className = hasSecondaryText
        ? 'tw:inline-flex tw:min-h-8 tw:w-full tw:min-w-[45px] tw:max-w-full tw:shrink tw:flex-col tw:items-center tw:justify-center tw:overflow-hidden tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:px-1.5 tw:py-1 tw:text-center tw:text-[11px] tw:font-semibold tw:leading-none tw:text-gray-900 tw:sm:w-auto tw:sm:max-w-28 tw:sm:shrink-0 tw:sm:px-2'
        : 'tw:inline-flex tw:min-h-6 tw:w-full tw:min-w-[45px] tw:max-w-full tw:shrink tw:items-center tw:justify-center tw:truncate tw:rounded-md tw:border tw:border-gray-300 tw:bg-white tw:px-1.5 tw:py-1 tw:text-[11px] tw:font-semibold tw:leading-none tw:text-gray-900 tw:sm:w-auto tw:sm:max-w-28 tw:sm:shrink-0 tw:sm:px-2';
      applyPocketSummaryBackground(pill, rowIndex);

      if (hasSecondaryText) {
        var primaryLine = document.createElement('span');
        var secondaryLine = document.createElement('span');
        primaryLine.className = 'tw:block tw:max-w-full tw:truncate';
        secondaryLine.className = 'tw:block tw:max-w-full tw:truncate';
        secondaryLine.style.fontSize = '6px';
        secondaryLine.style.lineHeight = '1';
        primaryLine.textContent = labelText.primary;
        secondaryLine.textContent = labelText.secondary;
        pill.appendChild(primaryLine);
        pill.appendChild(secondaryLine);
      } else if (labelText.primary) {
        pill.textContent = labelText.primary;
      } else {
        pill.innerHTML = '&nbsp;';
      }

      return pill;
    }

    function createPocketSummaryPillRow(row) {
      var line = document.createElement('span');
      var pills = document.createElement('span');
      var values = row.weekdays.length
        ? row.weekdays.map(function (day) { return { primary: day, secondary: row.pocketLabel }; })
        : blankValues(7);
      line.setAttribute('data-magnetic-pocket-summary-row', '');
      line.className = 'tw:flex tw:w-full tw:min-w-0 tw:items-start tw:gap-1.5 tw:sm:items-center';
      pills.className = 'tw:grid tw:w-full tw:min-w-0 tw:grid-cols-[repeat(auto-fit,minmax(2.15rem,1fr))] tw:gap-1 tw:sm:flex tw:sm:flex-wrap tw:sm:items-center';
      values.forEach(function (value) {
        pills.appendChild(createPocketSummaryPill(value, row.index));
      });
      line.appendChild(pills);
      return line;
    }

    function createPocketStepSummaryPreview(rows) {
      var wrap = document.createElement('span');
      wrap.setAttribute('data-magnetic-pocket-summary-wrap', '');
      wrap.className = 'tw:flex tw:w-full tw:min-w-0 tw:flex-col tw:gap-1';
      getPreviewStackSlots(rows).forEach(function (row) {
        wrap.appendChild(createPocketSummaryPillRow(row));
      });
      return wrap;
    }

    function renderPocketStepSummary() {
      var rows = getPocketPreviewRows();
      var selected = isPocketSelected();
      var summaryValue = selected
        ? rows.map(function (row) { return row.pocketLabel; }).join('|') + ':' + state.pocket.startDay
        : 'No pocket engraving';
      setStepSummaryContent('5', summaryValue, createPocketStepSummaryPreview(rows));
    }

    function updatePocketConfirm() {
      var confirm = q('[data-step-confirm="5"]');
      if (!confirm) return;
      var variant = findAddonVariant(pocketProduct, state.count);
      var selected = isPocketSelected();
      confirm.disabled = !isPocketModeAllowed(state.count, state.pocket.mode)
        || (selected && (!variant || !hasCompletePocketValues() || !hasValidPocketStart()));
    }

    function renderPocketStep() {
      var variant = findAddonVariant(pocketProduct, state.count);
      var isSingle = state.count === 1;
      var help = q('[data-pocket-help]');
      if (help) {
        help.textContent = isSingle
          ? 'Choose one Weekly or Daily pocket label, or Skip. Select the first weekday and the remaining weekday engravings follow in order. The shown add-on price is charged once for the entire set, not per case.'
          : 'Choose Weekly, Daily, or Skip. Weekly and Daily apply the shown label to each selected case. Select the first weekday and the remaining weekday engravings follow in order. The shown add-on price is charged once for the entire set, not per case.';
      }
      var singleModes = q('[data-pocket-single-modes]');
      var presetModes = q('[data-pocket-preset-modes]');
      if (singleModes) singleModes.classList.toggle('tw:hidden', !isSingle);
      if (presetModes) presetModes.classList.toggle('tw:hidden', isSingle);

      qa('[data-pocket-mode]').forEach(function (btn) {
        var mode = btn.getAttribute('data-pocket-mode');
        var engraved = mode !== 'NONE';
        applyModeButton(btn, state.pocket.mode === mode, engraved && !variant);
      });
      renderPocketButtonPrice(variant);

      var status = q('[data-pocket-status]');
      if (status) {
        var message = !pocketProduct
          ? 'In-pocket engraving is not configured. You can still skip this step.'
          : (!variant ? 'In-pocket engraving is unavailable for this case count. You can still skip this step.' : '');
        status.textContent = message;
        status.classList.toggle('tw:hidden', !message);
      }

      var selected = isPocketSelected();
      var fields = q('[data-pocket-fields]');
      if (fields) fields.classList.toggle('tw:hidden', !selected);
      var singleLabelField = q('[data-pocket-single-label-field]');
      if (singleLabelField) singleLabelField.classList.toggle('tw:hidden', !(isSingle && selected));
      renderSinglePocketLabelOptions();
      var start = q('[data-pocket-start]');
      if (start) start.value = hasValidPocketStart() ? state.pocket.startDay : 'SUN';
      renderPocketPreview();
      setPocketValidation('');
      updatePocketConfirm();
      updatePrice();
    }

    function onPocketModeClick(btn) {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      var mode = btn.getAttribute('data-pocket-mode');
      if (!isPocketModeAllowed(state.count, mode)) return;
      if (state.pocket.mode === mode) return;
      if (state.touched['5']) {
        setStepTouched('5', false);
        setStepSummary('5', '');
      }
      resetStepsAfter('5');
      state.pocket.mode = mode;
      if (mode !== 'SINGLE') state.pocket.singleLabel = '';
      if (!hasValidPocketStart()) state.pocket.startDay = 'SUN';
      renderPocketStep();
      refreshAtc();
    }

    function onPocketSingleLabel(select) {
      if (state.count !== 1 || state.pocket.mode !== 'SINGLE') return;
      var value = isAllowedSinglePocketLabel(select.value) ? select.value : '';
      if (state.pocket.singleLabel === value) return;
      if (state.touched['5']) {
        setStepTouched('5', false);
        setStepSummary('5', '');
      }
      resetStepsAfter('5');
      state.pocket.singleLabel = value;
      setPocketValidation('');
      renderPocketPreview();
      updatePocketConfirm();
      refreshAtc();
    }

    function onPocketStart(select) {
      if (!isPocketSelected() || WEEKDAYS.indexOf(select.value) === -1) return;
      if (state.touched['5']) {
        setStepTouched('5', false);
        setStepSummary('5', '');
      }
      resetStepsAfter('5');
      state.pocket.startDay = select.value;
      setPocketValidation('');
      renderPocketPreview();
      updatePocketConfirm();
      refreshAtc();
    }

    // ---- Step 6: Ultem Lid (setting-gated) -------------------------------

    function ultemStepSummaryText() {
      if (state.ultem.mode === 'SWAP') {
        return state.count === 1 ? 'Swapped to Ultem Lid' : 'Swapped to Ultem Lids';
      }
      if (state.ultem.mode === 'YES') {
        return state.count === 1 ? 'Yes, Add Ultem Lid' : 'Yes, Add Ultem Lids';
      }
      return 'No, Thanks';
    }

    function setUltemValidation(msg) {
      var el = q('[data-ultem-validation]');
      if (!el) return;
      el.textContent = msg || '';
      el.classList.toggle('tw:hidden', !msg);
    }

    function updateUltemConfirm() {
      var confirm = q('[data-step-confirm="6"]');
      if (!confirm) return;
      var variant = findUltemVariant();
      confirm.disabled = state.ultem.mode !== 'NONE'
        && !(state.ultem.mode === 'YES' && !!variant)
        && !(state.ultem.mode === 'SWAP' && isSwapSelectable());
    }

    function renderUltemStep() {
      if (!ultemStepVisible) return;
      var variant = ultemVisible ? findUltemVariant() : null;
      var swapSupported = isSwapSupportedForSelection();
      var swapReason = swapSupported ? getSwapBlockReason() : 'unsupported';
      var swapSelectable = swapReason === '';
      var deltaCents = swapSupported ? getSwapDeltaCents() : null;

      qa('[data-ultem-mode]').forEach(function (btn) {
        var mode = btn.getAttribute('data-ultem-mode');
        var selected = state.ultem.mode === mode;
        var unavailable = (mode === 'YES' && !variant) || (mode === 'SWAP' && !swapSelectable);
        var hidden = (mode === 'SWAP' && !swapSupported) || (mode === 'YES' && !ultemVisible);
        btn.classList.toggle('tw:hidden', hidden);
        btn.classList.toggle('tw:flex', !hidden);
        applyModeButton(btn, selected, unavailable);
        btn.classList.toggle('tw:cursor-pointer', !unavailable);
        btn.classList.toggle('tw:hover:bg-gray-50', !selected && !unavailable);
      });

      var offerLabel = q('[data-ultem-offer-label]');
      if (offerLabel) offerLabel.textContent = state.count === 1 ? 'Ultem lid' : 'Ultem lids';
      var yesLabel = q('[data-ultem-yes-label]');
      if (yesLabel) {
        yesLabel.textContent = state.count === 1
          ? 'Add Extra Ultem Lid'
          : 'Add Extra Ultem Lids - Set of ' + state.count;
      }
      var swapLabel = q('[data-ultem-swap-label]');
      if (swapLabel) {
        swapLabel.textContent = swapReason === 'soldout'
          ? (state.count === 1 ? 'Ultem Lid - Sold out' : 'Ultem Lids - Sold out')
          : (state.count === 1 ? 'Swap to Ultem Lid' : 'Swap to Ultem Lids');
      }
      var swapPrice = q('[data-ultem-swap-price]');
      if (swapPrice) {
        swapPrice.textContent = deltaCents === null || deltaCents === 0
          ? ''
          : (deltaCents > 0 ? '(+' + formatMoney(deltaCents) + ')' : '(-' + formatMoney(Math.abs(deltaCents)) + ')');
      }
      qa('[data-ultem-price]').forEach(function (el) {
        el.textContent = ultemVisible ? addonPriceLabel(variant) : '';
      });

      var status = q('[data-ultem-status]');
      if (status) {
        // Swap stays visible when it is sold out or missing a color, with the
        // reason next to it, and opens up again once stock arrives.
        var message = '';
        if (swapSupported && state.ultem.mode !== 'SWAP' && swapReason) {
          message = getSwapUnavailableNote(swapReason);
        } else if (ultemVisible && !variant) {
          message = !ultemProduct
            ? 'Ultem lids are unavailable right now. You can continue with No, Thanks.'
            : 'Ultem lids are unavailable for this case size and count. You can continue with No, Thanks.';
        }
        status.textContent = message;
        status.classList.toggle('tw:hidden', !message);
      }

      setUltemValidation('');
      updateUltemConfirm();
      updatePrice();

      // Ultem stock is a fetch, so render again when it lands.
      ensureSwapResolution().then(function () {
        if (state.ultem.mode === 'SWAP' && !isSwapSelectable()) {
          state.ultem.mode = 'NONE';
          setStepTouched('6', false);
          setStepSummary('6', '');
          renderUltemStep();
          refreshAtc();
          return;
        }
        if (swapState.key) renderUltemStepVisualsOnly();
      });
    }

    // Re-render after a stock fetch without starting another one.
    function renderUltemStepVisualsOnly() {
      var swapSupported = isSwapSupportedForSelection();
      var swapReason = swapSupported ? getSwapBlockReason() : 'unsupported';
      var swapSelectable = swapReason === '';
      qa('[data-ultem-mode]').forEach(function (btn) {
        var mode = btn.getAttribute('data-ultem-mode');
        if (mode !== 'SWAP') return;
        var selected = state.ultem.mode === mode;
        btn.classList.toggle('tw:hidden', !swapSupported);
        btn.classList.toggle('tw:flex', swapSupported);
        applyModeButton(btn, selected, !swapSelectable);
        btn.classList.toggle('tw:cursor-pointer', swapSelectable);
        btn.classList.toggle('tw:hover:bg-gray-50', !selected && swapSelectable);
      });
      var swapLabel = q('[data-ultem-swap-label]');
      if (swapLabel) {
        swapLabel.textContent = swapReason === 'soldout'
          ? (state.count === 1 ? 'Ultem Lid - Sold out' : 'Ultem Lids - Sold out')
          : (state.count === 1 ? 'Swap to Ultem Lid' : 'Swap to Ultem Lids');
      }
      var status = q('[data-ultem-status]');
      if (status && swapSupported && state.ultem.mode !== 'SWAP') {
        var message = getSwapUnavailableNote(swapReason);
        status.textContent = message;
        status.classList.toggle('tw:hidden', !message);
      }
      updateUltemConfirm();
      refreshAtc();
    }

    function onUltemModeClick(btn) {
      if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      var mode = btn.getAttribute('data-ultem-mode');
      if (mode !== 'YES' && mode !== 'NONE' && mode !== 'SWAP') return;
      if (mode === 'SWAP' && !isSwapSelectable()) return;
      if (state.ultem.mode === mode) return;
      if (state.touched['6']) {
        setStepTouched('6', false);
        setStepSummary('6', '');
      }
      state.ultem.mode = mode;
      renderUltemStep();
      refreshAtc();
    }

    // ---- confirm flow + add-to-cart --------------------------------------

    function confirmStep(step) {
      if (step === '1') {
        if (state.count < 1 || !hasSellableTierVariantForCount(state.count)) return;
        setStepTouched('1', true);
        setStepSummary('1', COUNT_SELECTION_SUMMARIES[state.count] || (state.count + ' cases'));
        resetStepsAfter('1');
        renderSizeSelection();
        setStepLocked('2', false);
        expandStep('2');
      } else if (step === '2') {
        if (!state.size) return;
        var availability = getCountSizeAvailability(state.count, state.size);
        if (!availability.ready || !availability.available) {
          setSizeValidation(availability.message || 'This size is unavailable for the selected case count.');
          return;
        }
        setStepTouched('2', true);
        setStepSummary('2', state.size.displayName);
        resetStepsAfter('2');
        setStepLocked('3', false);
        expandStep('3');
      } else if (step === '3') {
        if (!allSlotsChosen()) { setColorValidation('Please choose a color for every case.'); return; }
        if (!isSelectedAllocationAvailable()) {
          setColorValidation('Some selected colors no longer have enough stock. Please choose another color.');
          renderColorSlots();
          return;
        }
        setColorValidation('');
        setStepTouched('3', true);
        renderColorStepSummary();
        resetStepsAfter('3');
        setStepLocked('4', false);
        expandStep('4');
      } else if (step === '4') {
        if (state.lid.mode !== 'CUSTOM' && state.lid.mode !== 'NONE') {
          setLidValidation('Choose custom lid text or skip lid engraving.');
          return;
        }
        var lidVariant = findAddonVariant(lidProduct, state.count);
        var lidCount = selectedLidCount();
        if (state.lid.mode === 'CUSTOM' && (!lidVariant || lidCount < 1)) {
          setLidValidation(lidVariant
            ? 'Enter text for at least one lid, or choose Skip Lid Engraving.'
            : 'Lid engraving is unavailable for this case count.');
          return;
        }
        state.lid.values = state.lid.mode === 'CUSTOM' ? normalizedLidValues() : blankValues(state.count);
        setLidValidation('');
        setStepTouched('4', true);
        renderLidStepSummary();
        resetStepsAfter('4');
        setStepLocked('5', false);
        expandStep('5');
      } else if (step === '5') {
        var pocketSelected = isPocketSelected();
        if (!isPocketModeAllowed(state.count, state.pocket.mode)) {
          setPocketValidation(state.count === 1
            ? 'Choose Add Pocket Engraving or Skip Engraving.'
            : 'Choose Weekly, Daily, or Skip Engraving.');
          return;
        }
        var pocketVariant = findAddonVariant(pocketProduct, state.count);
        if (pocketSelected && !pocketVariant) {
          setPocketValidation('In-pocket engraving is unavailable for this case count.');
          return;
        }
        if (pocketSelected && !hasCompletePocketValues()) {
          setPocketValidation(state.count === 1
            ? 'Choose a pocket label.'
            : 'Please review the engraving preset.');
          return;
        }
        if (pocketSelected && !hasValidPocketStart()) {
          setPocketValidation('Choose a valid starting weekday.');
          return;
        }
        setPocketValidation('');
        setStepTouched('5', true);
        renderPocketStepSummary();
        resetStepsAfter('5');
        if (ultemStepVisible) {
          setStepLocked('6', false);
          expandStep('6');
          refreshAtc();
          return;
        }
        collapseAll();
        state.expandedStep = '';
        refreshAtc();
      } else if (step === '6') {
        if (state.ultem.mode !== 'YES' && state.ultem.mode !== 'NONE' && state.ultem.mode !== 'SWAP') {
          setUltemValidation('Choose one of the Ultem options or No, Thanks.');
          return;
        }
        var ultemVariant = findUltemVariant();
        if (state.ultem.mode === 'YES' && !ultemVariant) {
          setUltemValidation('Ultem is unavailable for this case size and count.');
          return;
        }
        if (state.ultem.mode === 'SWAP' && !isSwapSelectable()) {
          setUltemValidation(getSwapUnavailableNote(getSwapBlockReason()) || 'The Ultem swap is unavailable for this set.');
          return;
        }
        setUltemValidation('');
        setStepTouched('6', true);
        setStepSummary('6', ultemStepSummaryText());
        collapseAll();
        state.expandedStep = '';
        refreshAtc();
      }
      updatePrice();
    }

    function onStepHeaderClick(header) {
      var step = header.getAttribute('data-step-header');
      var el = getStepEl(step);
      if (!el || el.getAttribute('data-locked') === 'true') return;
      if (state.expandedStep === step) {
        confirmStep(step);
        return;
      }
      expandStep(step);
    }

    function isAtcReady() {
      var availability = state.size ? getCountSizeAvailability(state.count, state.size) : null;
      var tierVariant = findTierVariant(state.count, state.size && state.size.category);
      var swapping = state.ultem.mode === 'SWAP';
      var lidEngravingVariant = swapping
        ? findUltemLidEngravingVariant(state.count)
        : findAddonVariant(lidProduct, state.count);
      var lidReady = state.lid.mode === 'NONE'
        || (state.lid.mode === 'CUSTOM' && !!lidEngravingVariant && selectedLidCount() > 0);
      var pocketSelected = isPocketSelected();
      var pocketReady = state.pocket.mode === 'NONE'
        || (pocketSelected
          && !!findAddonVariant(pocketProduct, state.count)
          && hasCompletePocketValues()
          && hasValidPocketStart());
      var ultemReady = !ultemStepVisible
        || (state.touched['6']
          && (state.ultem.mode === 'NONE'
            || (state.ultem.mode === 'YES' && ultemVisible && !!findUltemVariant())
            || (state.ultem.mode === 'SWAP' && isSwapSelectable())));
      var packagingReady = !requiresPackagingForCount(state.count) || !!findPackagingVariant();
      return rolesConfigured
        && state.touched['1'] && state.touched['2'] && state.touched['3'] && state.touched['4'] && state.touched['5']
        && (!ultemStepVisible || state.touched['6'])
        && allSlotsChosen()
        && isSelectedAllocationAvailable()
        && !!(availability && availability.ready && availability.available)
        && isTierVariantSellable(tierVariant)
        && lidReady
        && pocketReady
        && ultemReady
        && packagingReady;
    }

    function refreshAtc() {
      var ready = isAtcReady();
      if (atcBtn) atcBtn.disabled = !ready;
      setAtcLabel(ready ? 'Add to Cart' : 'Configure Your Set First');
      updatePrice();
    }

    function formatMoney(cents) {
      var n = parseInt(cents, 10);
      if (!isFinite(n)) return '';
      if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
        return window.theme.Currency.formatMoney(n, (window.theme.moneyFormat || moneyFormat));
      }
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(n, moneyFormat);
      }
      return '$' + (n / 100).toFixed(2);
    }

    function buildDisplayAddons() {
      var lidVariant = findAddonVariant(lidProduct, state.count);
      var pocketVariant = findAddonVariant(pocketProduct, state.count);
      var ultemVariant = findUltemVariant();
      var lidQuantity = state.lid.mode === 'CUSTOM' ? selectedLidCount() : 0;
      var pocketQuantity = isPocketSelected() ? 1 : 0;
      var ultemQuantity = ultemVisible && state.ultem.mode === 'YES' ? 1 : 0;

      function entry(selected, variant, quantity) {
        var current = selected && variant ? priceCents(variant) * quantity : 0;
        var compare = selected && variant ? compareAtCents(variant) * quantity : 0;
        return {
          selected: !!selected,
          available: !!variant,
          variantId: variant ? String(variant.id) : null,
          quantity: selected ? quantity : 0,
          currentCents: current,
          compareAtCents: compare
        };
      }

      var addons = {
        lid: entry(lidQuantity > 0, lidVariant, lidQuantity),
        pocket: entry(pocketQuantity > 0, pocketVariant, pocketQuantity),
        ultem: entry(ultemQuantity > 0, ultemVariant, ultemQuantity)
      };
      return {
        detail: addons,
        entries: [
          { variant: addons.lid.selected ? lidVariant : null, quantity: addons.lid.quantity },
          { variant: addons.pocket.selected ? pocketVariant : null, quantity: addons.pocket.quantity },
          { variant: addons.ultem.selected ? ultemVariant : null, quantity: addons.ultem.quantity }
        ]
      };
    }

    function buildDisplayAvailability() {
      if (!rolesConfigured || !tier || !tier.variants || !tier.variants.length) {
        return { status: 'outstock', available: false, complete: false, label: 'This configuration is unavailable.' };
      }
      if (!state.count || !state.size) {
        return { status: 'neutral', available: false, complete: false, label: 'Configure to check availability.' };
      }
      if (requiresPackagingForCount(state.count) && !findPackagingVariant()) {
        return { status: 'outstock', available: false, complete: true, label: 'Unavailable for this configuration.' };
      }
      if (availabilityLoading || !cartReservationsReady) {
        return { status: 'checking', available: false, complete: false, label: 'Checking availability…' };
      }

      var status = getCountSizeAvailability(state.count, state.size);
      if (!status.ready) {
        return { status: 'checking', available: false, complete: false, label: 'Checking availability…' };
      }
      if (!status.available || (allSlotsChosen() && !isSelectedAllocationAvailable())) {
        return { status: 'outstock', available: false, complete: true, label: 'Unavailable for this configuration.' };
      }
      return { status: 'instock', available: true, complete: true, label: 'In stock' };
    }

    function buildDisplayState() {
      // With Swap chosen, the set price is the Ultem Material variant.
      var tierVariant = getSelectedTierVariant();
      var addonProjection = buildDisplayAddons();
      var totals = computeDisplayTotals(tierVariant, addonProjection.entries);
      var roles = ROLE_LAYOUT[state.count] || [];
      var labels = SLOT_LABELS[state.count] || [];
      var lidValues = state.lid.mode === 'CUSTOM' ? normalizedLidValues() : blankValues(state.count);
      return {
        count: state.count || null,
        size: state.size ? {
          key: state.size.key,
          label: state.size.label,
          category: state.size.category,
          displayName: state.size.displayName
        } : null,
        colors: roles.map(function (role, index) {
          var selected = state.colors[index];
          var previewVariant = findPreviewVariant(role, index);
          return {
            slotIndex: index,
            role: role,
            label: labels[index] || ('Case ' + (index + 1)),
            colorName: selected ? selected.colorName : null,
            variantId: selected ? String(selected.variantId) : null,
            imageUrl: getPreviewCaseSrc(previewVariant, role) || null,
            lidText: lidValues[index] || ''
          };
        }),
        addOns: addonProjection.detail,
        pocket: {
          mode: state.pocket.mode,
          confirmed: !!state.touched['5']
        },
        totals: totals,
        availability: buildDisplayAvailability(),
        tierVariantId: tierVariant ? String(tierVariant.id) : null,
        confirmedSteps: Object.assign({}, state.touched)
      };
    }

    function renderPdpChrome(display) {
      var figure = q('[data-price-figure]');
      var prompt = q('[data-price-prompt]');
      var total = q('[data-price-total]');
      var compare = q('[data-price-compare]');
      var savings = q('[data-price-savings]');
      var totals = display.totals;

      if (figure && prompt) {
        figure.classList.toggle('tw:hidden', !totals.hasPriceBasis);
        prompt.classList.toggle('tw:hidden', totals.hasPriceBasis);
      }
      if (total) total.textContent = totals.hasPriceBasis ? formatMoney(totals.currentTotalCents) : '';
      if (compare) {
        compare.textContent = totals.savingsCents > 0 ? formatMoney(totals.compareTotalCents) : '';
        compare.classList.toggle('tw:hidden', totals.savingsCents <= 0);
      }
      if (savings) {
        savings.textContent = totals.savingsCents > 0 ? 'Save ' + formatMoney(totals.savingsCents) : '';
        savings.classList.toggle('tw:hidden', totals.savingsCents <= 0);
      }

      var stockPill = q('[data-stock-pill]');
      var stockLabel = q('[data-stock-label]');
      if (stockPill) stockPill.setAttribute('data-stock-status', display.availability.status);
      if (stockLabel) stockLabel.textContent = display.availability.label;
    }

    function renderCaseSpecifications(display) {
      if (!caseSpecsEl) return;
      var count = (display && display.count) || 1;
      var category = (display && display.size && display.size.category) || 'pill';
      var matched = false;
      for (var i = 0; i < caseSpecPanels.length; i++) {
        var panel = caseSpecPanels[i];
        var show = count >= 1 && count <= 4 && !!category
          && Number(panel.getAttribute('data-case-spec-count')) === count
          && panel.getAttribute('data-case-spec-size-category') === category;
        panel.classList.toggle('tw:hidden', !show);
        if (show) matched = true;
      }
      caseSpecsEl.classList.toggle('tw:hidden', !matched);
    }

    function updatePrice() {
      var display = buildDisplayState();
      renderPdpChrome(display);
      renderLivePreview(display);
      renderCaseSpecifications(display);
      section.dispatchEvent(new CustomEvent('magnetic-set:state', { detail: display }));
    }

    function setAtcLabel(text) { var l = atcBtn && atcBtn.querySelector('[data-atc-label]'); if (l) l.textContent = text; }
    function setAtcSpinner(v) { var s = atcBtn && atcBtn.querySelector('[data-atc-spinner]'); if (s) s.classList.toggle('tw:hidden', !v); }

    function setAtcError(msg) {
      var box = q('[data-atc-error]');
      var m = q('[data-atc-error-message]');
      if (!box) return;
      if (msg) { if (m) m.textContent = msg; box.classList.remove('tw:hidden'); }
      else { if (m) m.textContent = ''; box.classList.add('tw:hidden'); }
    }

    function invalidateColorSelection(message) {
      setStepTouched('3', false);
      setStepSummary('3', '');
      resetStepsAfter('3');
      setStepLocked('3', false);
      expandStep('3');
      setColorValidation(message || 'Some selected colors no longer have enough stock. Please choose another color.');
      refreshAtc();
    }

    function onAtcClick() {
      if (!isAtcReady()) return;
      var swapping = state.ultem.mode === 'SWAP';
      var tierVariant = getSelectedTierVariant();
      if (!tierVariant) { setAtcError('We could not find a matching product variant. Please review your selections.'); return; }
      if (swapping && !isSwapSelectable()) {
        setAtcError(getSwapUnavailableNote(getSwapBlockReason()) || 'Please review the Ultem lid step.');
        return;
      }

      // Build the c array in ROLE_LAYOUT order: state.colors is already indexed
      // by slot, and slots render in ROLE_LAYOUT order, so map preserves it.
      var roles = ROLE_LAYOUT[state.count] || [];
      var c = [];
      for (var i = 0; i < state.count; i++) {
        var sel = state.colors[i];
        if (!sel || !sel.variantId) { setAtcError('Please choose a color for every case.'); return; }
        c.push(String(sel.variantId));
      }
      if (c.length !== roles.length) { setAtcError('Color selection is incomplete.'); return; }

      var lidValues = state.lid.mode === 'CUSTOM' ? normalizedLidValues() : blankValues(state.count);
      // A Swapped set engraves Ultem lids, which are their own catalog rows at
      // the same price, so `lbc` below is unchanged.
      var lidVariant = state.lid.mode === 'CUSTOM'
        ? (swapping ? findUltemLidEngravingVariant(state.count) : findAddonVariant(lidProduct, state.count))
        : null;
      var engravedLids = lidValues.filter(Boolean).length;
      if (state.lid.mode === 'CUSTOM' && (!lidVariant || engravedLids < 1)) {
        setAtcError('Please review the lid engraving step.');
        return;
      }

      var pocketSelected = isPocketSelected();
      var resolvedPockets = pocketSelected ? pocketValues() : blankValues(state.count);
      var pocketVariant = pocketSelected ? findAddonVariant(pocketProduct, state.count) : null;
      if (pocketSelected && (!pocketVariant || !hasCompletePocketValues() || !hasValidPocketStart())) {
        setAtcError('Please review the in-pocket engraving step.');
        return;
      }

      var ultemVariant = (ultemVisible && state.ultem.mode === 'YES') ? findUltemVariant() : null;
      if (ultemVisible && state.ultem.mode === 'YES' && !ultemVariant) {
        setAtcError('Please review the Ultem lid step.');
        return;
      }

      var packagingVariant = findPackagingVariant();
      if (requiresPackagingForCount(state.count) && !packagingVariant) {
        setAtcError('Packaging is unavailable for this configuration. Please try another size or case count.');
        return;
      }

      if (!window.theme || !theme.twoSideCart) { setAtcError('Cart is not ready yet. Please try again.'); return; }

      setAtcError('');
      atcBtn.disabled = true;
      setAtcLabel('Adding...');
      setAtcSpinner(true);

      var bundleId = theme.twoSideCart.createBundleId();
      var needed = uniqueRoles(ROLE_LAYOUT[state.count] || []);
      var roleRefresh = Promise.all(needed.map(function (role) {
        return fetchRolePayload(role, true);
      }));
      Promise.all([roleRefresh, loadCartItems()]).then(function (results) {
        var items = results[1];
        setCartReservations(items);
        // Ultem stock can move while the set is being built, so re-read it
        // against the cart we just loaded rather than the page-load cache.
        if (!swapping) return results;
        return ensureSwapResolution({ force: true }).then(function () {
          if (isSwapSelectable()) return results;
          var message = getSwapUnavailableNote(getSwapBlockReason())
            || 'The Ultem cases are no longer available for this set.';
          state.ultem.mode = 'NONE';
          setStepTouched('6', false);
          setStepSummary('6', '');
          setStepLocked('6', false);
          expandStep('6');
          renderUltemStep();
          setUltemValidation(message);
          throw new Error(message);
        });
      }).then(function (results) {
        var items = results[1];
        var availability = getCountSizeAvailability(state.count, state.size);
        if (!availability.ready || !availability.available) {
          if (availability.reason === 'color_catalog_empty') {
            state.colors = [];
            invalidateColorSelection('No colors are available for this selection.');
          } else {
            invalidateSelectedSize(availability.message);
          }
          throw new Error(availability.message || 'This count and size are no longer available.');
        }
        if (!isSelectedAllocationAvailable()) {
          invalidateColorSelection('Stock changed for one of your selected colors. Please review the color step.');
          throw new Error('Stock changed for one of your selected colors. Please review your selections.');
        }
        var capturedCurrency = cartCurrency
          || normalizeCurrencyCode(window.Shopify && Shopify.currency && Shopify.currency.active)
          || normalizeCurrencyCode(theme.shopCurrency);
        var usdPrices = USD_ADDON_CENTS_BY_COUNT[state.count];
        if (!capturedCurrency || !usdPrices) {
          throw new Error('Currency pricing could not be resolved. Please try again.');
        }
        // `ubc` is what the transform converts from in a third currency, so a
        // missing size price must block the add rather than ship a zero base.
        var ultemBaseCents = ultemVariant
          ? usdUltemSetCents(state.count, state.size && state.size.category)
          : 0;
        if (ultemVariant && !ultemBaseCents) {
          throw new Error('Ultem pricing could not be resolved. Please try again.');
        }
        // Swapped expands into the Ultem pieces instead of the aluminum ones,
        // in the same ROLE_LAYOUT slot order. The transform splits the Ultem
        // set price across them, so every line matches its listing price.
        var caseIds = c;
        if (swapping) {
          caseIds = getSwapVariantIdsInSlotOrder();
          if (!caseIds) {
            throw new Error('The Ultem cases could not be resolved. Please review the Ultem lid step.');
          }
        }
        var payload = {
          c: caseIds,
          l: lidValues,
          p: resolvedPockets,
          ps: pocketSelected ? state.pocket.startDay : '',
          cc: capturedCurrency,
          bc: 'USD',
          // `tc` keeps compatibility with a previously deployed transform.
          tc: parseInt(tierVariant.price, 10) || 0,
          lc: lidVariant ? (parseInt(lidVariant.price, 10) || 0) : 0,
          pc: pocketVariant ? (parseInt(pocketVariant.price, 10) || 0) : 0,
          uc: ultemVariant ? (parseInt(ultemVariant.price, 10) || 0) : 0,
          lbc: lidVariant ? usdPrices.lid : 0,
          pbc: pocketVariant ? usdPrices.pocket : 0,
          ubc: ultemBaseCents,
          lv: lidVariant ? String(lidVariant.id) : '',
          pv: pocketVariant ? String(pocketVariant.id) : '',
          uv: ultemVariant ? String(ultemVariant.id) : '',
          pkv: packagingVariant ? String(packagingVariant.id) : ''
        };
        var setNumber = String(theme.twoSideCart.getNextMagneticSetNumber(items));
        var properties = {
          '_ikg_bundle': 'mset2:' + bundleId,
          '_magnetic_set': JSON.stringify(payload),
          'Magnetic Set #': setNumber
        };
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: tierVariant.id, quantity: 1, properties: properties }] })
        });
      }).then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw new Error(err.description || err.message || 'Add to cart failed'); });
        return res.json();
      }).then(function () {
        window.location.href = '/cart';
      }).catch(function (err) {
        setAtcError(String((err && err.message) || 'Add to cart failed. Please try again.'));
        setAtcSpinner(false);
        refreshAtc();
      });
    }

    // ---- events + init ----------------------------------------------------

    function bindEvents() {
      section.addEventListener('magnetic-set:state', function (event) {
        var detail = event.detail || {};
        syncDetailsSectionForSelection(
          detail.count,
          detail.pocket && detail.pocket.mode,
          { initial: !detailsInitialStateHandled }
        );
        detailsInitialStateHandled = true;
      });

      section.addEventListener('pointerdown', function (e) {
        var target = e.target;
        if (!target || typeof target.closest !== 'function') return;
        var trigger = target.closest('[data-color-trigger]');
        if (trigger && section.contains(trigger)) prepareColorPopupFromTrigger(trigger);
      }, true);
      section.addEventListener('keydown', onColorKeydown);
      section.addEventListener('keydown', function (e) {
        if (e.key === 'Tab' && notifyModalEl && notifyModalEl.getAttribute('data-open') === 'true') {
          trapNotifyFocus(e);
          return;
        }
        if (e.key === 'Escape' && notifyModalEl && notifyModalEl.getAttribute('data-open') === 'true') {
          e.preventDefault();
          closeNotifyModal();
          return;
        }
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var compareTrigger = e.target && e.target.closest && e.target.closest('[data-compare-tooltip]');
        if (!compareTrigger || !section.contains(compareTrigger)) return;
        e.preventDefault();
        e.stopPropagation();
        toggleCompareTooltip(compareTrigger);
      });
      section.addEventListener('click', function (e) {
        var t = e.target;
        if (!t || typeof t.closest !== 'function') return;
        var notifyOpen = t.closest('[data-notify-open]');
        if (notifyOpen && section.contains(notifyOpen)) {
          e.preventDefault();
          openNotifyModal({ returnFocus: notifyOpen });
          return;
        }
        var notifyClose = t.closest('[data-notify-close]');
        if (notifyClose && section.contains(notifyClose)) {
          e.preventDefault();
          closeNotifyModal();
          return;
        }
        if (notifyModalEl && t === notifyModalEl) {
          closeNotifyModal();
          return;
        }
        var galleryThumb = t.closest('[data-gallery-thumb]');
        if (galleryThumb && section.contains(galleryThumb)) {
          e.preventDefault();
          setActiveGallerySlide(galleryThumb.getAttribute('data-slide-target'));
          return;
        }
        var galleryPrev = t.closest('[data-gallery-prev]');
        if (galleryPrev && section.contains(galleryPrev)) {
          e.preventDefault();
          if (!galleryPrev.disabled) moveGallery(-1);
          return;
        }
        var galleryNext = t.closest('[data-gallery-next]');
        if (galleryNext && section.contains(galleryNext)) {
          e.preventDefault();
          if (!galleryNext.disabled) moveGallery(1);
          return;
        }
        var countBtn = t.closest('[data-count-option]');
        if (countBtn && section.contains(countBtn)) { onCountClick(countBtn); return; }
        var sizeBtn = t.closest('[data-size-option]');
        if (sizeBtn && section.contains(sizeBtn)) { onSizeClick(sizeBtn); return; }
        var colorBtn = t.closest('[data-color-option]');
        if (colorBtn && section.contains(colorBtn)) { onColorClick(colorBtn); return; }
        var lidMode = t.closest('[data-lid-mode]');
        if (lidMode && section.contains(lidMode)) { onLidModeClick(lidMode); return; }
        var pocketMode = t.closest('[data-pocket-mode]');
        if (pocketMode && section.contains(pocketMode)) { onPocketModeClick(pocketMode); return; }
        var ultemMode = t.closest('[data-ultem-mode]');
        if (ultemMode && section.contains(ultemMode)) { onUltemModeClick(ultemMode); return; }
        var retry = t.closest('[data-color-retry]');
        if (retry && section.contains(retry)) { e.preventDefault(); onStep3Expand(true); return; }
        var sizeRetry = t.closest('[data-size-retry]');
        if (sizeRetry && section.contains(sizeRetry)) { e.preventDefault(); prepareSizeAvailability(true); return; }
        var compareTrigger = t.closest('[data-compare-tooltip]');
        if (compareTrigger && section.contains(compareTrigger)) {
          e.preventDefault();
          e.stopPropagation();
          toggleCompareTooltip(compareTrigger);
          return;
        }
        var confirmBtn = t.closest('[data-step-confirm]');
        if (confirmBtn && section.contains(confirmBtn)) { e.preventDefault(); confirmStep(confirmBtn.getAttribute('data-step-confirm')); return; }
        var header = t.closest('[data-step-header]');
        if (header && section.contains(header)) { e.preventDefault(); onStepHeaderClick(header); return; }
        if (t === atcBtn || (atcBtn && atcBtn.contains(t))) { onAtcClick(); return; }
      });
      section.addEventListener('input', function (e) {
        var input = e.target;
        if (input && input.matches && input.matches('[data-notify-email]')) {
          setNotifyMessage('', 'neutral');
          return;
        }
        if (input && input.matches && input.matches('[data-lid-input]')) onLidInput(input);
      });
      section.addEventListener('change', function (e) {
        var select = e.target;
        if (!select || !select.matches) return;
        if (select.matches('[data-notify-count]')) {
          notifyRoleLabelOverride = '';
          notifyRequestedColor = '';
          setNotifyMessage('', 'neutral');
          refreshNotifySizeOptions('', '', '');
          return;
        }
        if (select.matches('[data-notify-size]')) {
          notifyRequestedColor = '';
          setNotifyMessage('', 'neutral');
          refreshNotifyRoleOptions('', '');
          return;
        }
        if (select.matches('[data-notify-role]')) {
          notifyRoleLabelOverride = '';
          notifyRequestedColor = '';
          setNotifyMessage('', 'neutral');
          refreshNotifyColorOptions('');
          return;
        }
        if (select.matches('[data-notify-color]')) {
          setNotifyMessage('', 'neutral');
          syncNotifyHiddenFields();
          return;
        }
        if (select.matches('[data-pocket-single-label]')) { onPocketSingleLabel(select); return; }
        if (select.matches('[data-pocket-start]')) onPocketStart(select);
      });
      if (notifyFormEl) notifyFormEl.addEventListener('submit', onNotifySubmit);
      if (galleryStage) {
        galleryStage.addEventListener('keydown', function (event) {
          if (event.target !== galleryStage) return;
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            moveGallery(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            moveGallery(1);
          }
        });
        galleryStage.addEventListener('pointerdown', beginGallerySwipe);
        galleryStage.addEventListener('pointerup', endGallerySwipe);
        galleryStage.addEventListener('pointercancel', cancelGallerySwipe);
        galleryStage.addEventListener('lostpointercapture', cancelGallerySwipe);
      }
      if (previewWidthQuery) {
        if (typeof previewWidthQuery.addEventListener === 'function') {
          previewWidthQuery.addEventListener('change', refreshLivePreviewWidth);
        } else if (typeof previewWidthQuery.addListener === 'function') {
          previewWidthQuery.addListener(refreshLivePreviewWidth);
        }
      }
    }

    function init() {
      bindEvents();
      setActiveGallerySlide(0);
      if (!rolesConfigured) {
        if (atcBtn) atcBtn.disabled = true;
        updatePrice();
        return;
      }
      if (!tier || !tier.variants || !tier.variants.length) {
        console.warn('[magnetic-set] Missing tier product variants; inline JSON not found.');
        if (atcBtn) atcBtn.disabled = true;
        updatePrice();
        return;
      }
      if (countIdx < 0 || sizeIdx < 0) {
        console.warn('[magnetic-set] Could not resolve Case Count / Case Size options by name.');
      }
      if (materialIdx >= 0) {
        var unknownMaterials = [];
        tier.variants.forEach(function (v) {
          if (tierMaterial(v) !== null) return;
          var value = String((v.options || [])[materialIdx] || '');
          if (unknownMaterials.indexOf(value) === -1) unknownMaterials.push(value);
        });
        if (unknownMaterials.length) {
          console.warn(
            '[magnetic-set] Ignoring tier variants with an unrecognized Material value: '
              + unknownMaterials.join(', ')
              + '. Expected "Aluminum" or "Ultem".'
          );
        }
      }
      sizes = buildSizes();
      var initialCount = parseInitialCount(window.location.search);
      if (initialCount && hasSellableTierVariantForCount(initialCount)) {
        state.count = initialCount;
        state.lid.values = blankValues(initialCount);
      }
      renderCountSelection();
      renderSizeOptions();
      fetchSizePresentation(false).then(function (payload) {
        if (!payload) return;
        applySizePresentation([]);
        renderSizeSelection();
      });
      setStepLocked('2', true);
      setStepLocked('3', true);
      setStepLocked('4', true);
      setStepLocked('5', true);
      if (ultemStepVisible) setStepLocked('6', true);
      applyStepVisual('1');
      fetchCartReservations(false);
      refreshAtc();
    }

    init();
  }

  function initRoot(el) {
    if (!el || el.__msInit) return;
    el.__msInit = true;
    initSection(el);
  }

  function boot() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      ROLE_LAYOUT: ROLE_LAYOUT,
      COUNT_SELECTION_SUMMARIES: COUNT_SELECTION_SUMMARIES,
      SLOT_LABELS: SLOT_LABELS,
      POCKET_PRESETS: POCKET_PRESETS,
      SINGLE_POCKET_LABEL_GROUPS: SINGLE_POCKET_LABEL_GROUPS,
      SINGLE_POCKET_LABELS: SINGLE_POCKET_LABELS,
      USD_ADDON_CENTS_BY_COUNT: USD_ADDON_CENTS_BY_COUNT,
      USD_ULTEM_LID_CENTS_BY_SIZE: USD_ULTEM_LID_CENTS_BY_SIZE,
      usdUltemSetCents: usdUltemSetCents,
      sortColorsByAvailability: sortColorsByAvailability,
      getLidPreviewLabelStyle: getLidPreviewLabelStyle,
      parseInitialCount: parseInitialCount,
      getMagneticDetailsPageHandle: getMagneticDetailsPageHandle,
      isAllowedSinglePocketLabel: isAllowedSinglePocketLabel,
      isPocketModeAllowed: isPocketModeAllowed,
      isPocketEngravingMode: isPocketEngravingMode,
      resolvePocketValues: resolvePocketValues,
      orderedWeekdays: orderedWeekdays,
      buildPocketPreviewRows: buildPocketPreviewRows,
      computeDisplayTotals: computeDisplayTotals,
      resolveUltemVariant: resolveUltemVariant,
      requiresPackagingForCount: requiresPackagingForCount,
      resolvePackagingVariant: resolvePackagingVariant,
      buildPreviewAltText: buildPreviewAltText,
      getPreviewStackSlots: getPreviewStackSlots,
      getStorefrontInventoryFloor: getStorefrontInventoryFloor
    };
  }

  if (typeof document === 'undefined') return;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var target = e && e.target;
    if (!target) return;
    if (typeof target.matches === 'function' && target.matches(ROOT_SELECTOR)) initRoot(target);
    if (typeof target.querySelector === 'function') initRoot(target.querySelector(ROOT_SELECTOR));
  });
})();
